import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Badge } from './ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from './ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from './ui/dialog';
import { ArrowLeft, Package, ShoppingCart, Users, BarChart3, Plus, Edit, Trash2, Eye, EyeOff, Search, Mail, Star, CheckCircle, XCircle, Clock, Percent, Pin, FileText, Download, Bell } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';

const AdminPanel = ({ navigateHome }) => {
  const { user, isAuthenticated, getIdToken, testLogin } = useAuth();
  const [activeTab, setActiveTab] = useState("products");
  const [products, setProducts] = useState([]);
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showProductForm, setShowProductForm] = useState(false);
  const [editingProduct, setEditingProduct] = useState(null);
  const [isAdmin, setIsAdmin] = useState(false);
  const [checkingAdmin, setCheckingAdmin] = useState(true);

  // Insert-pricing config state (Phase 2)
  const [insertPricing, setInsertPricing] = useState([]);
  const [insertPricingLoading, setInsertPricingLoading] = useState(false);
  const [insertPricingMessage, setInsertPricingMessage] = useState(null);

  // Files tab: detail modal state
  const [fileDetail, setFileDetail] = useState(null); // { kind: 'quote' | 'service', record }

  // Product waitlist insight state
  const [waitlistCounts, setWaitlistCounts] = useState({}); // { productId: number }
  const [waitlistModal, setWaitlistModal] = useState(null); // { product, entries, loading }

  // Bulk inventory editor state
  const [bulkStockOpen, setBulkStockOpen] = useState(false);
  const [bulkStockDraft, setBulkStockDraft] = useState({});   // { [productId]: string }
  const [bulkStockSearch, setBulkStockSearch] = useState('');
  const [bulkStockSaving, setBulkStockSaving] = useState(false);

  const openBulkStockEditor = () => {
    const draft = {};
    (products || []).forEach(p => {
      draft[p.id] = String(p.stock_quantity ?? 0);
    });
    setBulkStockDraft(draft);
    setBulkStockSearch('');
    setBulkStockOpen(true);
  };

  const setBulkStockValue = (productId, value) => {
    // Keep raw string while typing; validate/clamp on save
    setBulkStockDraft(prev => ({ ...prev, [productId]: value }));
  };

  const saveBulkStock = async () => {
    // Only send rows that actually changed; coerce to a non-negative integer
    const updates = [];
    (products || []).forEach(p => {
      const raw = bulkStockDraft[p.id];
      if (raw === undefined) return;
      const parsed = Math.max(0, Math.floor(Number(raw)));
      if (!Number.isFinite(parsed)) return;
      if (parsed !== (p.stock_quantity ?? 0)) {
        updates.push({ product_id: p.id, stock_quantity: parsed });
      }
    });
    if (updates.length === 0) {
      alert('No changes to save.');
      return;
    }
    setBulkStockSaving(true);
    try {
      const res = await makeAuthenticatedRequest('/api/admin/products/bulk-stock', {
        method: 'PUT',
        body: JSON.stringify({ updates }),
      });
      if (!res.ok) throw new Error(`${res.status}`);
      const data = await res.json();
      alert(`Saved. ${data.updated} product${data.updated === 1 ? '' : 's'} updated.`);
      setBulkStockOpen(false);
      fetchProducts();
    } catch (err) {
      console.error('bulk stock save error', err);
      alert('Failed to save inventory changes.');
    } finally {
      setBulkStockSaving(false);
    }
  };

  // Product reviews manager state
  const [reviewMgrProduct, setReviewMgrProduct] = useState(null); // product being managed
  const [reviewList, setReviewList] = useState([]);
  const [reviewListLoading, setReviewListLoading] = useState(false);
  const emptyReviewForm = {
    id: null,
    user_name: '',
    rating: 5,
    title: '',
    comment: '',
    verified_purchase: true,
    status: 'approved',
  };
  const [reviewForm, setReviewForm] = useState(emptyReviewForm);
  const [reviewSaving, setReviewSaving] = useState(false);

  const openReviewManager = async (product) => {
    setReviewMgrProduct(product);
    setReviewForm(emptyReviewForm);
    setReviewList([]);
    setReviewListLoading(true);
    try {
      const res = await makeAuthenticatedRequest(`/api/admin/products/${product.id}/reviews`);
      if (res.ok) {
        const data = await res.json();
        if (Array.isArray(data.reviews)) setReviewList(data.reviews);
      }
    } catch (err) {
      console.error('load product reviews error', err);
    } finally {
      setReviewListLoading(false);
    }
  };

  const refreshReviewsForCurrentProduct = async () => {
    if (!reviewMgrProduct) return;
    try {
      const res = await makeAuthenticatedRequest(`/api/admin/products/${reviewMgrProduct.id}/reviews`);
      if (res.ok) {
        const data = await res.json();
        if (Array.isArray(data.reviews)) setReviewList(data.reviews);
      }
    } catch (err) {
      console.error('refresh reviews error', err);
    }
  };

  const saveReview = async () => {
    if (!reviewMgrProduct) return;
    if (!reviewForm.user_name.trim() || !reviewForm.title.trim() || !reviewForm.comment.trim()) {
      alert('Reviewer name, title and comment are required.');
      return;
    }
    setReviewSaving(true);
    try {
      const body = {
        user_name: reviewForm.user_name.trim(),
        rating: Number(reviewForm.rating),
        title: reviewForm.title.trim(),
        comment: reviewForm.comment.trim(),
        verified_purchase: !!reviewForm.verified_purchase,
        status: reviewForm.status || 'approved',
      };
      const url = reviewForm.id
        ? `/api/admin/reviews/${reviewForm.id}`
        : `/api/admin/products/${reviewMgrProduct.id}/reviews`;
      const method = reviewForm.id ? 'PUT' : 'POST';
      const res = await makeAuthenticatedRequest(url, {
        method,
        body: JSON.stringify(body),
      });
      if (!res.ok) throw new Error(`${res.status}`);
      setReviewForm(emptyReviewForm);
      await refreshReviewsForCurrentProduct();
      fetchProducts();
    } catch (err) {
      console.error('save review error', err);
      alert('Failed to save review.');
    } finally {
      setReviewSaving(false);
    }
  };

  const deleteReview = async (review_id) => {
    if (!window.confirm('Delete this review?')) return;
    try {
      const res = await makeAuthenticatedRequest(`/api/admin/reviews/${review_id}`, { method: 'DELETE' });
      if (!res.ok) throw new Error(`${res.status}`);
      await refreshReviewsForCurrentProduct();
      fetchProducts();
      // Also refresh the top-level Reviews tab list if it's been loaded
      if (typeof loadReviews === 'function') {
        try { loadReviews(); } catch { /* noop */ }
      }
    } catch (err) {
      console.error('delete review error', err);
      alert('Failed to delete review.');
    }
  };

  // Load insert pricing whenever the Inserts tab is opened
  useEffect(() => {
    if (activeTab !== 'inserts' || !isAdmin) return;
    (async () => {
      try {
        setInsertPricingLoading(true);
        const idToken = await getIdToken();
        const res = await fetch(`${process.env.REACT_APP_BACKEND_URL || ''}/api/admin/insert-pricing`, {
          headers: { 'Authorization': `Bearer ${idToken}` }
        });
        if (!res.ok) throw new Error(await res.text());
        const data = await res.json();
        // Ensure a stable display order: threaded-metric, threaded-imperial, bushing-metric, bushing-imperial
        const order = [['threaded','metric'],['threaded','imperial'],['bushing','metric'],['bushing','imperial']];
        const sorted = order
          .map(([t,u]) => (data.rows || []).find(r => r.type === t && r.units === u))
          .filter(Boolean);
        setInsertPricing(sorted.length ? sorted : (data.rows || []));
      } catch (err) {
        setInsertPricingMessage({ type: 'error', text: `Load failed: ${err.message || err}` });
      } finally {
        setInsertPricingLoading(false);
      }
    })();
  }, [activeTab, isAdmin, getIdToken]);
  
  // Marketplace stats
  const [marketplaceStats, setMarketplaceStats] = useState({
    applications: 0,
    activeListings: 0,
    totalSales: 0
  });

  // Marketplace data
  const [pendingListings, setPendingListings] = useState([]);
  const [recentApplications, setRecentApplications] = useState([]);

  // Admin marketplace management state
  const [allListings, setAllListings] = useState([]);
  const [allDesigners, setAllDesigners] = useState([]);
  const [selectedListing, setSelectedListing] = useState(null);
  const [selectedDesigner, setSelectedDesigner] = useState(null);
  const [showListingEditor, setShowListingEditor] = useState(false);
  const [showDesignerEditor, setShowDesignerEditor] = useState(false);
  const [marketplaceView, setMarketplaceView] = useState('overview'); // overview, listings, designers

  
  // Contact submissions
  const [contactSubmissions, setContactSubmissions] = useState([]);

  // Inventory management state
  const [inventory, setInventory] = useState([]);
  const [showInventoryForm, setShowInventoryForm] = useState(false);
  const [selectedProductForInventory, setSelectedProductForInventory] = useState(null);
  const [inventoryAdjustment, setInventoryAdjustment] = useState({
    quantity_change: '',
    reason: '',
    notes: ''
  });

  // Reviews management state
  const [reviews, setReviews] = useState([]);
  const [reviewFilter, setReviewFilter] = useState('all');

  // CMS Editor state
  const [cmsPages, setCmsPages] = useState([]);
  const [selectedPage, setSelectedPage] = useState(null);
  const [showCmsEditor, setShowCmsEditor] = useState(false);
  const [cmsForm, setCmsForm] = useState({
    title: '',
    content: '',
    meta_description: '',
    is_published: true
  });


  // User Management state
  const [allUsers, setAllUsers] = useState([]);
  const [userSearchTerm, setUserSearchTerm] = useState('');
  const [selectedUser, setSelectedUser] = useState(null);
  const [showUserEditor, setShowUserEditor] = useState(false);

  // Newsletter Management state
  const [newsletterSubscribers, setNewsletterSubscribers] = useState([]);
  const [newsletterSearchTerm, setNewsletterSearchTerm] = useState('');


  // Order Update state
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [showOrderUpdateModal, setShowOrderUpdateModal] = useState(false);
  const [orderUpdateForm, setOrderUpdateForm] = useState({
    status: '',
    tracking_number: '',
    tracking_carrier: '',
    note: '',
    internal_note: '',
    notify_customer: true
  });
  const [orderHistory, setOrderHistory] = useState([]);

  // Lead time adjustment state
  const [leadTimeAdjustment, setLeadTimeAdjustment] = useState({
    value: 0,
    unit: 'days'
  });
  const [savingLeadTime, setSavingLeadTime] = useState(false);

  // Discount settings state
  const [discountSettings, setDiscountSettings] = useState({
    hiddenDiscount: 0,
    visibleDiscount: 0,
    visibleDiscountName: 'Promotional Pricing'
  });
  const [savingDiscount, setSavingDiscount] = useState(false);


  // Product form state
  const [productForm, setProductForm] = useState({
    name: '',
    description: '',
    manufacturer: '',
    product_type: 'FDM Printer',
    category: '',
    price: '',
    image_urls: [], // Changed from image_url to image_urls array
    stock_quantity: '',
    notify_me_enabled: false,
    sku: '',
    specifications: {
      build_volume: '',
      layer_height: '',
      print_speed: '',
      connectivity: '',
      material_compatibility: '',
      diameter: '',
      color: '',
      weight: ''
    },
    tags: ''
  });

  useEffect(() => {
    if (isAuthenticated) {
      checkAdminStatus();
    }
  }, [isAuthenticated]);

  const checkAdminStatus = async () => {
    try {
      // Test admin bypass for development
      if (user?.email === 'test@admin.com') {
        console.log('Test admin detected - granting admin access');
        setIsAdmin(true);
        loadProducts();
        loadOrders();
        loadMarketplaceStats();
        loadInventory();
        loadReviews();
        loadCmsPages();
        loadNewsletterSubscribers();
        setCheckingAdmin(false);
        return;
      }
      
      // Check if user is admin by trying to access an admin endpoint
      const response = await makeAuthenticatedRequest('/api/orders/admin/all');
      if (response.ok) {
        setIsAdmin(true);
        loadProducts();
        loadOrders();
        loadMarketplaceStats(); // Load marketplace data
        loadInventory();
        loadReviews();
        loadCmsPages();
        loadNewsletterSubscribers();
      } else if (response.status === 403) {
        setIsAdmin(false);
      }
    } catch (error) {
      console.error('Error checking admin status:', error);
      setIsAdmin(false);
    } finally {
      setCheckingAdmin(false);
    }
  };

  const loadMarketplaceStats = async () => {
    try {
      // Load designer applications
      const appsResponse = await makeAuthenticatedRequest('/api/marketplace/applications');
      const applications = appsResponse.ok ? (await appsResponse.json()).applications || [] : [];
      
      // Filter for PENDING applications only (those needing admin review) 
      const pendingApplications = applications.filter(app => app.status === 'pending');
      
      // Get recent applications for display (all statuses for admin visibility)
      const sortedApplications = applications.sort((a, b) => new Date(b.created_at) - new Date(a.created_at));
      
      // Load ALL marketplace listings for admin (including drafts)
      // Since /api/marketplace/listings only returns active listings, we need admin access
      const listingsResponse = await makeAuthenticatedRequest('/api/admin/marketplace/listings');
      const listings = listingsResponse.ok ? (await listingsResponse.json()).listings || [] : [];
      
      // Filter for draft and active listings (draft = pending approval)
      const pendingListingsData = listings.filter(l => l.status === 'draft');
      const activeListings = listings.filter(l => l.status === 'active').length;
      const totalSales = listings.reduce((sum, l) => sum + (l.total_earnings || 0), 0);
      
      // Update states
      setMarketplaceStats({
        applications: pendingApplications.length, // Only pending applications
        activeListings: activeListings,
        totalSales: totalSales
      });
      
      // Set pending listings and recent applications for display
      setPendingListings(pendingListingsData);
      setRecentApplications(sortedApplications.slice(0, 5)); // Show last 5 applications (any status)
      
    } catch (error) {
      console.error('Error loading marketplace stats:', error);
    }
  };

  const loadNewsletterSubscribers = async () => {
    try {
      const response = await makeAuthenticatedRequest('/api/admin/newsletter/subscribers');
      if (response.ok) {
        const data = await response.json();
        setNewsletterSubscribers(data.subscribers || []);
      }
    } catch (error) {
      console.error('Error loading newsletter subscribers:', error);
    }
  };

  const removeNewsletterSubscriber = async (email) => {
    if (!window.confirm(`Remove ${email} from newsletter?`)) return;
    
    try {
      const response = await makeAuthenticatedRequest(
        `/api/admin/newsletter/subscribers/${encodeURIComponent(email)}`,
        { method: 'DELETE' }
      );
      
      if (response.ok) {
        alert('Subscriber removed successfully');
        loadNewsletterSubscribers();
      } else {
        alert('Failed to remove subscriber');
      }
    } catch (error) {
      console.error('Error removing subscriber:', error);
      alert('Error removing subscriber');
    }
  };


  // ========== ADMIN MARKETPLACE/DESIGNER MANAGEMENT ==========
  const loadAllListings = async () => {
    try {
      const response = await makeAuthenticatedRequest('/api/admin/marketplace/listings');
      if (response.ok) {
        const data = await response.json();
        setAllListings(data.listings || []);
      }
    } catch (error) {
      console.error('Error loading all listings:', error);
    }
  };

  const loadAllDesigners = async () => {
    try {
      const response = await makeAuthenticatedRequest('/api/admin/designers');
      if (response.ok) {
        const data = await response.json();
        setAllDesigners(data.designers || []);
      }
    } catch (error) {
      console.error('Error loading all designers:', error);
    }
  };

  const deleteListing = async (listingId) => {
    if (!window.confirm('Are you sure you want to delete this listing? This action cannot be undone.')) return;
    
    const reason = prompt('Enter deletion reason (optional):');
    // If user cancels the prompt, reason will be null, so abort
    if (reason === null) return;

    try {
      const response = await makeAuthenticatedRequest(
        `/api/admin/marketplace/listings/${listingId}?reason=${encodeURIComponent(reason || '')}`,
        { method: 'DELETE' }
      );
      if (response.ok) {
        alert('Listing deleted successfully!');
        loadAllListings();
      } else {
        alert('Error deleting listing: ' + response.statusText);
      }
    } catch (error) {
      console.error('Delete listing error:', error);
      alert('Error deleting listing: ' + error.message);
    }
  };

  const suspendDesigner = async (designerId) => {
    const reason = prompt('Enter reason for freezing this designer (e.g., "Under review", "Policy violation"):');
    if (!reason) return;
    
    if (!window.confirm(`Are you sure you want to freeze this designer?\n\nReason: ${reason}\n\nThis will pause all their active listings.`)) return;

    try {
      const response = await makeAuthenticatedRequest(
        `/api/admin/designers/${designerId}/suspend`,
        'POST',
        { reason }
      );
      if (response.ok) {
        alert('Designer frozen successfully! Their listings have been paused.');
        loadAllDesigners();
      }
    } catch (error) {
      alert('Error freezing designer');
    }
  };

  const unsuspendDesigner = async (designerId) => {
    if (!window.confirm('Are you sure you want to reactivate this designer?\n\nThis will allow them to manage listings again.')) return;
    
    try {
      const response = await makeAuthenticatedRequest(
        `/api/admin/designers/${designerId}/unsuspend`,
        'POST'
      );
      if (response.ok) {
        alert('Designer reactivated successfully!');
        loadAllDesigners();
      }
    } catch (error) {
      alert('Error reactivating designer');
    }
  };

  const removeDesignerStatus = async (designerId) => {
    const reason = prompt('Enter reason for removing designer status:');
    if (!reason) return;
    if (!window.confirm('This will permanently remove designer privileges. Continue?')) return;

    try {
      const response = await makeAuthenticatedRequest(
        `/api/admin/designers/${designerId}/remove`,
        'DELETE',
        { reason }
      );
      if (response.ok) {
        alert('Designer status removed successfully!');
        loadAllDesigners();
      }
    } catch (error) {
      alert('Error removing designer status');
    }
  };


  // ========== USER MANAGEMENT FUNCTIONS ==========
  const loadAllUsers = async () => {
    try {
      const response = await makeAuthenticatedRequest('/api/admin/users');
      if (response.ok) {
        const data = await response.json();
        setAllUsers(data.users || []);
      }
    } catch (error) {
      console.error('Error loading users:', error);
    }
  };

  const banUser = async (userId) => {
    const reason = prompt('Enter ban reason:');
    if (!reason) return;
    if (!window.confirm('Are you sure you want to ban this user?')) return;

    try {
      const response = await makeAuthenticatedRequest(
        `/api/admin/users/${userId}/ban`,
        {
          method: 'POST',
          body: JSON.stringify({ reason })
        }
      );
      if (response.ok) {
        alert('User banned successfully!');
        loadAllUsers();
      }
    } catch (error) {
      alert('Error banning user');
    }
  };

  const unbanUser = async (userId) => {
    try {
      const response = await makeAuthenticatedRequest(`/api/admin/users/${userId}/unban`, { method: 'POST' });
      if (response.ok) {
        alert('User unbanned successfully!');
        loadAllUsers();
      }
    } catch (error) {
      alert('Error unbanning user');
    }
  };

  // ========== ROLE MANAGEMENT FUNCTIONS ==========
  const assignRoleToUser = async (userEmail, role) => {
    try {
      setLoading(true);
      const response = await makeAuthenticatedRequest('/api/admin/assign-role', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ user_email: userEmail, role: role })
      });

      if (response.ok) {
        const data = await response.json();
        alert(`✅ ${data.message}\n\nRole "${role}" has been assigned to ${userEmail}`);
        setShowUserEditor(false);
        setSelectedUser(null);
        loadAllUsers(); // Refresh the user list
      } else {
        const error = await response.json();
        alert(`❌ Error: ${error.detail || 'Failed to assign role'}`);
      }
    } catch (error) {
      console.error('Error assigning role:', error);
      alert('❌ Error assigning role. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const getRolePermissions = (role) => {
    const permissions = {
      'SUPER_ADMIN': 'All permissions including role management',
      'ADMIN': 'Product & order management, analytics',
      'MANAGER': 'Order management, view products & analytics',
      'STAFF': 'View products & orders only',
      'CUSTOMER': 'Standard user access'
    };
    return permissions[role] || 'Standard user access';
  };

  // ========== FILE DOWNLOAD FUNCTIONS ==========
  const [quotes, setQuotes] = useState([]);
  const [serviceRequests, setServiceRequests] = useState([]);

  const loadQuotes = async () => {
    try {
      const response = await makeAuthenticatedRequest('/api/admin/quotes/all');
      if (response.ok) {
        const data = await response.json();
        setQuotes(data.quotes || []);
      }
    } catch (error) {
      console.error('Error loading quotes:', error);
    }
  };

  const loadServiceRequests = async () => {
    try {
      const response = await makeAuthenticatedRequest('/api/admin/service-requests/all');
      if (response.ok) {
        const data = await response.json();
        setServiceRequests(data.requests || []);
      }
    } catch (error) {
      console.error('Error loading service requests:', error);
    }
  };

  const downloadQuoteFile = async (quoteId) => {
    try {
      const idToken = await getIdToken();
      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL || ''}/api/admin/download/quote/${quoteId}`,
        {
          headers: { 'Authorization': `Bearer ${idToken}` }
        }
      );
      
      if (response.ok) {
        // Extract filename from Content-Disposition header
        const contentDisposition = response.headers.get('Content-Disposition');
        let filename = `quote_${quoteId}.file`; // Default fallback
        
        if (contentDisposition) {
          const filenameMatch = contentDisposition.match(/filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/);
          if (filenameMatch && filenameMatch[1]) {
            filename = filenameMatch[1].replace(/['"]/g, '');
          }
        }
        
        const blob = await response.blob();
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = filename;
        document.body.appendChild(a);
        a.click();
        window.URL.revokeObjectURL(url);
        document.body.removeChild(a);
      } else {
        alert('Error downloading file');
      }
    } catch (error) {
      alert('Error downloading file');
    }
  };

  const downloadServiceFile = async (requestId) => {
    try {
      const idToken = await getIdToken();
      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL || ''}/api/admin/download/service/${requestId}`,
        {
          headers: { 'Authorization': `Bearer ${idToken}` }
        }
      );
      
      if (response.ok) {
        // Extract filename from Content-Disposition header
        const contentDisposition = response.headers.get('Content-Disposition');
        let filename = `service_${requestId}.file`; // Default fallback
        
        if (contentDisposition) {
          const filenameMatch = contentDisposition.match(/filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/);
          if (filenameMatch && filenameMatch[1]) {
            filename = filenameMatch[1].replace(/['"]/g, '');
          }
        }
        
        const blob = await response.blob();
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = filename;
        document.body.appendChild(a);
        a.click();
        window.URL.revokeObjectURL(url);
        document.body.removeChild(a);
      } else {
        alert('Error downloading file');
      }
    } catch (error) {
      alert('Error downloading file');
    }
  };

  // Generic authenticated file download helper — used by the new CSV/JSON exports.
  const downloadAuthenticatedFile = async (path, fallbackFilename) => {
    try {
      const idToken = await getIdToken();
      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL || ''}${path}`,
        { headers: { 'Authorization': `Bearer ${idToken}` } }
      );
      if (!response.ok) throw new Error(`HTTP ${response.status}`);
      const cd = response.headers.get('Content-Disposition');
      let filename = fallbackFilename;
      if (cd) {
        const m = cd.match(/filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/);
        if (m && m[1]) filename = m[1].replace(/['"]/g, '');
      }
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
    } catch (err) {
      console.error('Download error:', err);
      alert('Download failed. Please try again.');
    }
  };

  const exportQuotesCsv = () => downloadAuthenticatedFile('/api/admin/quotes/export.csv', 'quotes.csv');
  const exportServiceRequestsCsv = () => downloadAuthenticatedFile('/api/admin/service-requests/export.csv', 'service-requests.csv');
  const exportQuoteJson = (id) => downloadAuthenticatedFile(`/api/admin/quotes/${id}/export.json`, `quote-${id.slice(0,8)}.json`);
  const exportServiceRequestJson = (id) => downloadAuthenticatedFile(`/api/admin/service-requests/${id}/export.json`, `service-request-${id.slice(0,8)}.json`);


  // ========== ORDER UPDATE FUNCTIONS ==========
  const openOrderUpdate = (order) => {
    setSelectedOrder(order);
    setOrderUpdateForm({
      status: order.status || '',
      tracking_number: order.tracking_number || '',
      tracking_carrier: order.tracking_carrier || '',
      note: '',
      internal_note: '',
      notify_customer: true
    });
    loadOrderHistory(order.id);
    setShowOrderUpdateModal(true);
  };

  const loadOrderHistory = async (orderId) => {
    try {
      const response = await makeAuthenticatedRequest(`/api/admin/orders/${orderId}/history`);
      if (response.ok) {
        const data = await response.json();
        setOrderHistory(data.history || []);
      }
    } catch (error) {
      console.error('Error loading order history:', error);
    }
  };

  const submitOrderUpdate = async () => {
    if (!selectedOrder) return;

    try {
      const response = await makeAuthenticatedRequest(
        `/api/admin/orders/${selectedOrder.id}/update`,
        {
          method: 'POST',
          body: JSON.stringify(orderUpdateForm)
        }
      );

      if (response.ok) {
        alert('Order updated successfully!');
        setShowOrderUpdateModal(false);
        setSelectedOrder(null);
        loadOrders();
      } else {
        const error = await response.json();
        alert('Error: ' + (error.detail || 'Failed to update order'));
      }
    } catch (error) {
      console.error('Error updating order:', error);
      alert('Error updating order');
    }
  };

  const addOrderNote = async (note, isInternal = false) => {
    if (!selectedOrder || !note.trim()) return;

    try {
      const response = await makeAuthenticatedRequest(
        `/api/admin/orders/${selectedOrder.id}/note`,
        {
          method: 'POST',
          body: JSON.stringify({ note, is_internal: isInternal })
        }
      );

      if (response.ok) {
        alert('Note added successfully!');
        loadOrderHistory(selectedOrder.id);
      } else {
        alert('Error adding note');
      }
    } catch (error) {
      console.error('Error adding note:', error);
      alert('Error adding note');
    }
  };

  const handleApproveListing = async (listingId) => {
    try {
      const response = await makeAuthenticatedRequest(`/api/admin/marketplace/listings/${listingId}/approve`, {
        method: 'POST'
      });

      if (response.ok) {
        alert('Listing approved successfully!');
        loadMarketplaceStats(); // Refresh the data
      } else {
        const error = await response.json();
        alert(`Failed to approve listing: ${error.detail || 'Unknown error'}`);
      }
    } catch (error) {
      console.error('Error approving listing:', error);
      alert('Failed to approve listing. Please try again.');
    }
  };

  const handleRejectListing = async (listingId) => {
    const reason = prompt('Please provide a reason for rejecting this listing (optional):');
    
    try {
      const url = `/api/admin/marketplace/listings/${listingId}/reject${reason ? `?reason=${encodeURIComponent(reason)}` : ''}`;
      const response = await makeAuthenticatedRequest(url, {
        method: 'POST'
      });

      if (response.ok) {
        alert('Listing rejected successfully!');
        loadMarketplaceStats(); // Refresh the data
      } else {
        const error = await response.json();
        alert(`Failed to reject listing: ${error.detail || 'Unknown error'}`);
      }
    } catch (error) {
      console.error('Error rejecting listing:', error);
      alert('Failed to reject listing. Please try again.');
    }
  };

  const handleApproveApplication = async (applicationId) => {
    try {
      const response = await makeAuthenticatedRequest(`/api/marketplace/applications/${applicationId}`, {
        method: 'PATCH',
        body: JSON.stringify({
          status: 'approved'
        })
      });

      if (response.ok) {
        alert('Designer application approved successfully!');
        loadMarketplaceStats(); // Refresh the data
      } else {
        const error = await response.json();
        alert(`Failed to approve application: ${error.detail || 'Unknown error'}`);
      }
    } catch (error) {
      console.error('Error approving application:', error);
      alert('Failed to approve application. Please try again.');
    }
  };

  const handleRejectApplication = async (applicationId) => {
    const notes = prompt('Please provide a reason for rejecting this application (optional):');
    
    try {
      const body = { status: 'rejected' };
      if (notes) {
        body.admin_notes = notes;
      }

      const response = await makeAuthenticatedRequest(`/api/marketplace/applications/${applicationId}`, {
        method: 'PATCH',
        body: JSON.stringify(body)
      });

      if (response.ok) {
        alert('Designer application rejected successfully!');
        loadMarketplaceStats(); // Refresh the data
      } else {
        const error = await response.json();
        alert(`Failed to reject application: ${error.detail || 'Unknown error'}`);
      }
    } catch (error) {
      console.error('Error rejecting application:', error);
      alert('Failed to reject application. Please try again.');
    }
  };

  useEffect(() => {
    if (isAuthenticated) {
      loadProducts();
      loadOrders();
      loadContactSubmissions();
      loadLeadTimeAdjustment();
      loadDiscountSettings();
    }
  }, [isAuthenticated]);

  const makeAuthenticatedRequest = async (url, options = {}) => {
    const idToken = await getIdToken();
    if (!idToken) {
      throw new Error('Authentication required');
    }

    // Use environment backend URL for all users (including test admin)
    const backendUrl = process.env.REACT_APP_BACKEND_URL || '';
    let apiUrl = backendUrl;
    if (apiUrl.includes('None') || !apiUrl || apiUrl === '') {
      apiUrl = '';
    }

    return fetch(`${apiUrl}${url}`, {
      ...options,
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${idToken}`,
        ...options.headers
      },
      credentials: 'include'
    });
  };

  const handleImageUpload = async (event) => {
    const files = Array.from(event.target.files);
    if (files.length === 0) return;

    try {
      const formData = new FormData();
      files.forEach(file => {
        formData.append('files', file);
      });

      const idToken = await getIdToken();
      if (!idToken) {
        alert('Authentication required');
        return;
      }

      const backendUrl = process.env.REACT_APP_BACKEND_URL || '';
      let apiUrl = backendUrl;
      if (apiUrl.includes('None') || !apiUrl || apiUrl === '') {
        apiUrl = '';
      }

      const response = await fetch(`${apiUrl}/api/admin/upload-product-images`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${idToken}`
        },
        body: formData
      });

      const result = await response.json();

      if (response.ok) {
        // Add new image URLs to the existing array with correct API prefix
        const backendUrl = process.env.REACT_APP_BACKEND_URL || '';
        let baseUrl = backendUrl;
        if (baseUrl.includes('None') || !baseUrl || baseUrl === '') {
          // For local development, use relative URLs
          baseUrl = '';
        }
        
        const newImageUrls = result.images.map(img => `${baseUrl}${img.url}`);
        
        setProductForm(prev => ({
          ...prev,
          image_urls: [...(prev.image_urls || []), ...newImageUrls]
        }));
        
        console.log('Uploaded image URLs:', newImageUrls); // Debug logging
        alert(`${result.image_count} image(s) uploaded successfully!`);
      } else {
        throw new Error(result.detail || 'Upload failed');
      }
    } catch (error) {
      console.error('Image upload error:', error);
      alert(`Failed to upload images: ${error.message}`);
    }
  };

  const loadProducts = async () => {
    setLoading(true);
    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL || ''}/api/products?per_page=500`);
      if (response.ok) {
        const data = await response.json();
        setProducts(data.products);
      }
    } catch (error) {
      console.error('Error loading products:', error);
    } finally {
      setLoading(false);
    }
    loadWaitlistSummary();
  };

  const loadWaitlistSummary = async () => {
    try {
      const res = await makeAuthenticatedRequest('/api/admin/products/waitlist/summary');
      if (res.ok) {
        const data = await res.json();
        setWaitlistCounts(data.counts || {});
      }
    } catch (err) {
      console.error('Error loading waitlist summary:', err);
    }
  };

  const openWaitlistModal = async (product) => {
    setWaitlistModal({ product, entries: [], loading: true });
    try {
      const res = await makeAuthenticatedRequest(`/api/admin/products/${product.id}/waitlist`);
      if (res.ok) {
        const data = await res.json();
        setWaitlistModal({ product, entries: data.entries || [], loading: false });
      } else {
        setWaitlistModal({ product, entries: [], loading: false });
      }
    } catch (err) {
      setWaitlistModal({ product, entries: [], loading: false });
    }
  };

  const loadOrders = async () => {
    try {
      const response = await makeAuthenticatedRequest('/api/orders/admin/all');
      if (response.ok) {
        const data = await response.json();
        setOrders(data.orders);
      }
    } catch (error) {
      console.error('Error loading orders:', error);
    }
  };

  // ========== LEAD TIME ADJUSTMENT FUNCTIONS ==========
  const loadLeadTimeAdjustment = async () => {
    try {
      const response = await makeAuthenticatedRequest('/api/admin/lead-time-adjustment');
      if (response.ok) {
        const data = await response.json();
        setLeadTimeAdjustment({
          value: data.value || 0,
          unit: data.unit || 'days'
        });
      }
    } catch (error) {
      console.error('Error loading lead time adjustment:', error);
    }
  };

  const saveLeadTimeAdjustment = async () => {
    setSavingLeadTime(true);
    try {
      const response = await makeAuthenticatedRequest('/api/admin/lead-time-adjustment', {
        method: 'POST',
        body: JSON.stringify({
          value: parseInt(leadTimeAdjustment.value) || 0,
          unit: leadTimeAdjustment.unit
        })
      });
      if (response.ok) {
        alert('Lead time adjustment saved successfully!');
      } else {
        alert('Failed to save lead time adjustment');
      }
    } catch (error) {
      console.error('Error saving lead time adjustment:', error);
      alert('Error saving lead time adjustment');
    } finally {
      setSavingLeadTime(false);
    }
  };

  // ========== DISCOUNT SETTINGS FUNCTIONS ==========
  const loadDiscountSettings = async () => {
    try {
      const response = await makeAuthenticatedRequest('/api/admin/discount-settings');
      if (response.ok) {
        const data = await response.json();
        setDiscountSettings({
          hiddenDiscount: data.hidden_discount || 0,
          visibleDiscount: data.visible_discount || 0,
          visibleDiscountName: data.visible_discount_name || 'Promotional Pricing'
        });
      }
    } catch (error) {
      console.error('Error loading discount settings:', error);
    }
  };

  const saveDiscountSettings = async () => {
    setSavingDiscount(true);
    try {
      const response = await makeAuthenticatedRequest('/api/admin/discount-settings', {
        method: 'POST',
        body: JSON.stringify({
          hidden_discount: parseFloat(discountSettings.hiddenDiscount) || 0,
          visible_discount: parseFloat(discountSettings.visibleDiscount) || 0,
          visible_discount_name: discountSettings.visibleDiscountName || 'Promotional Pricing'
        })
      });
      if (response.ok) {
        alert('Discount settings saved successfully!');
      } else {
        alert('Failed to save discount settings');
      }
    } catch (error) {
      console.error('Error saving discount settings:', error);
      alert('Error saving discount settings');
    } finally {
      setSavingDiscount(false);
    }
  };


  const loadContactSubmissions = async () => {
    try {
      const response = await makeAuthenticatedRequest('/api/admin/contact-submissions');
      if (response.ok) {
        const data = await response.json();
        setContactSubmissions(data.submissions || []);
      }
    } catch (error) {
      console.error('Error loading contact submissions:', error);
    }

  };

  // ========== INVENTORY MANAGEMENT FUNCTIONS ==========
  const loadInventory = async () => {
    try {
      const response = await makeAuthenticatedRequest('/api/admin/inventory');
      if (response.ok) {
        const data = await response.json();
        setInventory(data.inventory || []);
      }
    } catch (error) {
      console.error('Error loading inventory:', error);
    }
  };

  const handleInventoryAdjustment = async (productId) => {
    try {
      const response = await makeAuthenticatedRequest(
        `/api/admin/inventory/${productId}/adjust`,
        'POST',
        {
          product_id: productId,
          quantity_change: parseInt(inventoryAdjustment.quantity_change),
          reason: inventoryAdjustment.reason,
          notes: inventoryAdjustment.notes
        }
      );

      if (response.ok) {
        alert('Inventory updated successfully!');
        setShowInventoryForm(false);
        setInventoryAdjustment({ quantity_change: '', reason: '', notes: '' });
        loadInventory();
      } else {
        const error = await response.json();
        alert('Error: ' + (error.detail || 'Failed to update inventory'));
      }
    } catch (error) {
      console.error('Error adjusting inventory:', error);
      alert('Error adjusting inventory');
    }
  };

  // ========== REVIEWS MANAGEMENT FUNCTIONS ==========
  const loadReviews = async () => {
    try {
      const response = await makeAuthenticatedRequest('/api/admin/reviews');
      if (response.ok) {
        const data = await response.json();
        setReviews(data.reviews || []);
      }
    } catch (error) {
      console.error('Error loading reviews:', error);
    }
  };

  const moderateReview = async (reviewId, status, notes = '') => {
    try {
      const response = await makeAuthenticatedRequest(
        `/api/admin/reviews/${reviewId}`,
        'PATCH',
        { status, admin_notes: notes }
      );

      if (response.ok) {
        alert(`Review ${status} successfully!`);
        loadReviews();
      }
    } catch (error) {
      console.error('Error moderating review:', error);
      alert('Error moderating review');
    }
  };

  // ========== CMS EDITOR FUNCTIONS ==========
  const loadCmsPages = async () => {
    try {
      const response = await makeAuthenticatedRequest('/api/admin/cms/pages');
      if (response.ok) {
        const data = await response.json();
        setCmsPages(data.pages || []);
      }
    } catch (error) {
      console.error('Error loading CMS pages:', error);
    }
  };

  const handleCmsPageSelect = (page) => {
    setSelectedPage(page);
    setCmsForm({
      title: page.title,
      content: page.content,
      meta_description: page.meta_description || '',
      is_published: page.is_published
    });
    setShowCmsEditor(true);
  };

  const handleCmsSubmit = async (e) => {
    e.preventDefault();
    
    try {
      const isNewPage = selectedPage.slug === 'new' || !cmsPages.find(p => p.slug === selectedPage.slug);
      
      const endpoint = isNewPage 
        ? '/api/admin/cms/pages'
        : `/api/admin/cms/pages/${selectedPage.slug}`;
      
      const method = isNewPage ? 'POST' : 'PUT';
      
      const payload = isNewPage 
        ? { ...cmsForm, slug: selectedPage.slug, id: Date.now().toString() }
        : cmsForm;

      const response = await makeAuthenticatedRequest(
        endpoint,
        method,
        payload
      );

      if (response.ok) {
        alert(isNewPage ? 'Page created successfully!' : 'Page updated successfully!');
        setShowCmsEditor(false);
        setSelectedPage(null);
        loadCmsPages();
      } else {
        const error = await response.json();
        alert('Error: ' + (error.detail || `Failed to ${isNewPage ? 'create' : 'update'} page`));
      }
    } catch (error) {
      console.error('Error saving CMS page:', error);
      alert('Error saving page');
    }
  };

  const markContactAsRead = async (submissionId) => {
    try {
      const response = await makeAuthenticatedRequest(
        `/api/admin/contact-submissions/${submissionId}/mark-read`,
        { method: 'PATCH' }
      );
      if (response.ok) {
        // Reload submissions to reflect the change
        loadContactSubmissions();
      }
    } catch (error) {
      console.error('Error marking contact as read:', error);
    }
  };


  const handleProductSubmit = async (e) => {
    e.preventDefault();
    try {
      const productData = {
        ...productForm,
        price: parseFloat(productForm.price),
        stock_quantity: parseInt(productForm.stock_quantity),
        tags: productForm.tags.split(',').map(tag => tag.trim()).filter(tag => tag),
        image_url: productForm.image_urls?.[0] || '', // Use first image as primary for backward compatibility
        image_urls: productForm.image_urls || [] // Store all images
      };

      // Check if we're editing or creating
      const isEditing = editingProduct !== null;
      const url = isEditing ? `/api/products/${editingProduct.id}` : '/api/products';
      const method = isEditing ? 'PUT' : 'POST';

      const response = await makeAuthenticatedRequest(url, {
        method: method,
        body: JSON.stringify(productData)
      });

      if (response.ok) {
        alert(isEditing ? 'Product updated successfully!' : 'Product created successfully!');
        loadProducts();
        resetProductForm();
        setShowProductForm(false);
        setEditingProduct(null);
      } else {
        const error = await response.json();
        alert(`Error: ${error.detail}`);
      }
    } catch (error) {
      console.error('Error submitting product:', error);
      alert('Error submitting product');
    }
  };

  const resetProductForm = () => {
    setProductForm({
      name: '',
      description: '',
      manufacturer: '',
      product_type: 'FDM Printer',
      category: '',
      price: '',
      image_urls: [], // Reset to empty array
      stock_quantity: '',
      notify_me_enabled: false,
      sku: '',
      specifications: {
        build_volume: '',
        layer_height: '',
        print_speed: '',
        connectivity: '',
        material_compatibility: '',
        diameter: '',
        color: '',
        weight: ''
      },
      tags: ''
    });
    setEditingProduct(null);
    setShowProductForm(false);
  };

  const formatPrice = (price) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    }).format(price);
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getOrderStatusBadge = (status) => {
    const statusConfig = {
      pending: { color: 'bg-yellow-100 text-yellow-800', text: 'Pending' },
      confirmed: { color: 'bg-blue-100 text-blue-800', text: 'Confirmed' },
      in_production: { color: 'bg-purple-100 text-purple-800', text: 'In Production' },
      quality_check: { color: 'bg-indigo-100 text-indigo-800', text: 'Quality Check' },
      shipping: { color: 'bg-brand-orange-100 text-brand-orange-800', text: 'Shipping' },
      completed: { color: 'bg-brand-green-100 text-brand-green-800', text: 'Completed' },
      cancelled: { color: 'bg-red-100 text-red-800', text: 'Cancelled' }
    };

    const config = statusConfig[status] || statusConfig.pending;
    return <Badge className={`${config.color} border-0`}>{config.text}</Badge>;
  };

  const getStockStatusBadge = (quantity) => {
    if (quantity === 0) {
      return <Badge variant="destructive">Out of Stock</Badge>;
    } else if (quantity < 5) {
      return <Badge variant="outline" className="border-brand-orange-500 text-brand-orange-600">Low Stock</Badge>;
    } else {
      return <Badge variant="outline" className="border-green-500 text-green-600">In Stock</Badge>;
    }
  };

  if (!isAuthenticated) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100 flex items-center justify-center">
        <Card className="w-full max-w-md">
          <CardHeader className="text-center">
            <CardTitle>Admin Access Required</CardTitle>
            <CardDescription>Please log in to access the admin panel</CardDescription>
          </CardHeader>
          <CardContent className="text-center space-y-3">
            <Button onClick={navigateHome} variant="outline">
              Return to Home
            </Button>
            {/* Test Admin Access - Development Only */}
            <Button 
              onClick={testLogin}
              className="bg-orange-600 hover:bg-orange-700 w-full"
            >
              🔧 Test Admin Access (Dev Only)
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (checkingAdmin) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-purple-600 mx-auto mb-4"></div>
          <p className="text-slate-600">Checking admin permissions...</p>
        </div>
      </div>
    );
  }

  if (!isAdmin) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100 flex items-center justify-center">
        <Card className="w-full max-w-md">
          <CardHeader className="text-center">
            <CardTitle>Access Denied</CardTitle>
            <CardDescription>You don't have admin permissions for this panel</CardDescription>
          </CardHeader>
          <CardContent className="text-center space-y-4">
            <div className="text-sm text-slate-600">
              <p>Current user: {user?.email}</p>
              <p>Admin access is restricted to authorized personnel only.</p>
            </div>
            <Button onClick={navigateHome} variant="outline">
              Return to Home
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100">
      {/* Header */}
      <header className="bg-white/80 backdrop-blur-sm border-b border-slate-200 sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-4">
              <Button onClick={navigateHome} variant="ghost" className="flex items-center space-x-2">
                <ArrowLeft className="w-4 h-4" />
                <span>Back to Home</span>
              </Button>
              <div className="flex items-center space-x-3">
                <div className="w-8 h-8 bg-gradient-to-br from-purple-500 to-blue-600 rounded-lg flex items-center justify-center">
                  <BarChart3 className="w-5 h-5 text-white" />
                </div>
                <h1 className="text-2xl font-bold bg-gradient-to-r from-purple-600 to-blue-600 bg-clip-text text-transparent">
                  Admin Panel
                </h1>
              </div>
            </div>
            
            <div className="flex items-center space-x-2">
              <span className="text-sm text-slate-600">Welcome, {user?.name}</span>
            </div>
          </div>
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Admin Dashboard Tabs */}
        <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
          <TabsList className="flex flex-wrap w-full h-auto gap-1 mb-8 p-1">
            <TabsTrigger value="products" className="flex items-center space-x-2">
              <Package className="w-4 h-4" />
              <span>Products</span>
            </TabsTrigger>
            <TabsTrigger value="orders" className="flex items-center space-x-2">
              <ShoppingCart className="w-4 h-4" />
              <span>Orders</span>
            </TabsTrigger>
            <TabsTrigger value="marketplace" className="flex items-center space-x-2">
              <Users className="w-4 h-4" />
              <span>Marketplace</span>
            </TabsTrigger>
            <TabsTrigger value="contact" className="flex items-center space-x-2">
              <Mail className="w-4 h-4" />
              <span>Messages</span>
            </TabsTrigger>
            <TabsTrigger value="analytics" className="flex items-center space-x-2">
              <BarChart3 className="w-4 h-4" />
              <span>Analytics</span>
            </TabsTrigger>
            <TabsTrigger value="settings" className="flex items-center space-x-2">
              <Users className="w-4 h-4" />
              <span>Settings</span>
            </TabsTrigger>
            <TabsTrigger value="inventory" className="flex items-center space-x-2">
              <Package className="w-4 h-4" />
              <span>Inventory</span>
            </TabsTrigger>
            <TabsTrigger value="reviews" className="flex items-center space-x-2">
              <Star className="w-4 h-4" />
              <span>Reviews</span>
            </TabsTrigger>
            <TabsTrigger value="cms" className="flex items-center space-x-2">
              <Edit className="w-4 h-4" />
              <span>CMS Editor</span>
            </TabsTrigger>
            <TabsTrigger value="users" className="flex items-center space-x-2">
              <Users className="w-4 h-4" />
              <span>Users</span>
            </TabsTrigger>
            <TabsTrigger value="newsletter" className="flex items-center space-x-2">
              <Mail className="w-4 h-4" />
              <span>Newsletter</span>
            </TabsTrigger>
            <TabsTrigger value="files" className="flex items-center space-x-2">
              <Package className="w-4 h-4" />
              <span>Files</span>
            </TabsTrigger>
            <TabsTrigger value="inserts" className="flex items-center space-x-2">
              <Pin className="w-4 h-4" />
              <span>Inserts</span>
            </TabsTrigger>
          </TabsList>

          {/* Products Management Tab */}
          <TabsContent value="products">
            <div className="space-y-6">
              {/* Products Header */}
              <div className="flex items-center justify-between">
                <div>
                  <h2 className="text-2xl font-bold text-slate-800">Product Management</h2>
                  <p className="text-slate-600">Manage your 3D printing equipment and materials catalog</p>
                </div>
                <div className="flex items-center gap-2">
                  <Button
                    onClick={() => openBulkStockEditor()}
                    variant="outline"
                    className="text-[#0007b5] border-[#0007b5]/30 hover:bg-[#0007b5]/5"
                    data-testid="admin-bulk-stock-btn"
                  >
                    <Package className="w-4 h-4 mr-2" />
                    Bulk Edit Inventory
                  </Button>
                  <Button 
                    onClick={() => {
                      setShowProductForm(true);
                      // Scroll to top so user can see the form
                      setTimeout(() => {
                        window.scrollTo({ top: 0, behavior: 'smooth' });
                      }, 100);
                    }}
                    className="bg-gradient-to-r from-green-500 to-emerald-600 hover:from-green-600 hover:to-emerald-700"
                  >
                    <Plus className="w-4 h-4 mr-2" />
                    Add Product
                  </Button>
                </div>
              </div>

              {/* Product Creation Form */}
              {showProductForm && (
                <Card className="border-green-200">
                  <CardHeader>
                    <div className="flex items-center justify-between">
                      <div>
                        <CardTitle>{editingProduct ? 'Edit Product' : 'Add New Product'}</CardTitle>
                        <CardDescription>
                          {editingProduct ? 'Update product information and images' : 'Create a new product listing for the catalog'}
                        </CardDescription>
                      </div>
                      <Button 
                        type="button" 
                        variant="outline" 
                        onClick={resetProductForm}
                        size="sm"
                      >
                        Cancel
                      </Button>
                    </div>
                  </CardHeader>
                  <CardContent>
                    <form onSubmit={handleProductSubmit} className="space-y-4">
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div>
                          <label className="block text-sm font-medium mb-2">Product Name</label>
                          <Input
                            value={productForm.name}
                            onChange={(e) => setProductForm({...productForm, name: e.target.value})}
                            placeholder="e.g., Bambu Lab X1 Carbon"
                            required
                          />
                        </div>
                        
                        <div>
                          <label className="block text-sm font-medium mb-2">Manufacturer</label>
                          <Input
                            value={productForm.manufacturer}
                            onChange={(e) => setProductForm({...productForm, manufacturer: e.target.value})}
                            placeholder="e.g., Bambu Lab"
                            required
                          />
                        </div>

                        <div>
                          <label className="block text-sm font-medium mb-2">Product Type</label>
                          <Select value={productForm.product_type} onValueChange={(value) => setProductForm({...productForm, product_type: value})}>
                            <SelectTrigger>
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="FDM Printer">FDM Printer</SelectItem>
                              <SelectItem value="Resin Printer">Resin Printer</SelectItem>
                              <SelectItem value="Filament">Filament</SelectItem>
                              <SelectItem value="Accessory">Accessory</SelectItem>
                            </SelectContent>
                          </Select>
                        </div>

                        <div>
                          <label className="block text-sm font-medium mb-2">Category</label>
                          <Input
                            value={productForm.category}
                            onChange={(e) => setProductForm({...productForm, category: e.target.value})}
                            placeholder="e.g., Professional FDM"
                            required
                          />
                        </div>

                        <div>
                          <label className="block text-sm font-medium mb-2">Price ($)</label>
                          <Input
                            type="number"
                            step="0.01"
                            value={productForm.price}
                            onChange={(e) => setProductForm({...productForm, price: e.target.value})}
                            placeholder="299.99"
                            required
                          />
                        </div>

                        <div>
                          <label className="block text-sm font-medium mb-2">Stock Quantity</label>
                          <Input
                            type="number"
                            value={productForm.stock_quantity}
                            onChange={(e) => setProductForm({...productForm, stock_quantity: e.target.value})}
                            placeholder="10"
                            required
                          />
                          <label className="mt-3 flex items-start gap-2 cursor-pointer select-none" data-testid="notify-me-enabled-label">
                            <input
                              type="checkbox"
                              checked={!!productForm.notify_me_enabled}
                              onChange={(e) => setProductForm({...productForm, notify_me_enabled: e.target.checked})}
                              className="mt-0.5 w-4 h-4 rounded border-slate-300 text-[#0007b5] focus:ring-[#0007b5]"
                              data-testid="notify-me-enabled-checkbox"
                            />
                            <span className="text-sm text-slate-700">
                              Show <strong>Notify Me</strong> instead of <em>Out of Stock</em> when inventory is 0
                              <span className="block text-xs text-slate-500">Customers can sign up for an email when this item is back in stock.</span>
                            </span>
                          </label>
                        </div>

                        <div>
                          <label className="block text-sm font-medium mb-2">SKU</label>
                          <Input
                            value={productForm.sku}
                            onChange={(e) => setProductForm({...productForm, sku: e.target.value})}
                            placeholder="BBL-X1C"
                            required
                          />
                        </div>

                        <div>
                          <label className="block text-sm font-medium mb-2">Product Images</label>
                          
                          {/* Multiple Images Preview */}
                          {productForm.image_urls && productForm.image_urls.length > 0 && (
                            <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-4">
                              {productForm.image_urls.map((imageUrl, index) => (
                                <div key={index} className="relative group">
                                  <img
                                    src={imageUrl}
                                    alt={`Product image ${index + 1}`}
                                    className="w-24 h-24 object-cover rounded-lg border border-slate-200"
                                    onError={(e) => {
                                      e.target.style.display = 'none';
                                      e.target.nextSibling.style.display = 'block';
                                    }}
                                  />
                                  <div className="hidden w-24 h-24 bg-slate-100 rounded-lg border border-slate-200 flex items-center justify-center">
                                    <Package className="w-6 h-6 text-slate-400" />
                                  </div>
                                  
                                  {/* Remove Image Button */}
                                  <button
                                    type="button"
                                    onClick={() => {
                                      setProductForm(prev => ({
                                        ...prev,
                                        image_urls: prev.image_urls.filter((_, i) => i !== index)
                                      }));
                                    }}
                                    className="absolute -top-2 -right-2 bg-red-500 hover:bg-red-600 text-white rounded-full w-6 h-6 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity"
                                  >
                                    ×
                                  </button>
                                </div>
                              ))}
                            </div>
                          )}
                          
                          {/* Upload Options */}
                          <div className="space-y-3">
                            {/* Multiple File Upload */}
                            <div>
                              <input
                                type="file"
                                accept="image/*"
                                onChange={handleImageUpload}
                                multiple
                                className="hidden"
                                id="product-images-upload"
                              />
                              <Button
                                type="button"
                                variant="outline"
                                onClick={() => document.getElementById('product-images-upload').click()}
                                className="w-full flex items-center space-x-2"
                              >
                                <Package className="w-4 h-4" />
                                <span>Upload Images (Max 10)</span>
                              </Button>
                            </div>
                            
                            {/* URL Input Alternative */}
                            <div>
                              <label className="block text-xs text-slate-500 mb-1">Or add image URL:</label>
                              <div className="flex space-x-2">
                                <Input
                                  placeholder="https://images.unsplash.com/..."
                                  className="text-sm flex-1"
                                  id="manual-image-url"
                                />
                                <Button
                                  type="button"
                                  variant="outline"
                                  size="sm"
                                  onClick={() => {
                                    const urlInput = document.getElementById('manual-image-url');
                                    const url = urlInput.value.trim();
                                    if (url) {
                                      setProductForm(prev => ({
                                        ...prev,
                                        image_urls: [...(prev.image_urls || []), url]
                                      }));
                                      urlInput.value = '';
                                    }
                                  }}
                                >
                                  Add
                                </Button>
                              </div>
                            </div>
                            
                            {/* Image Count Display */}
                            <p className="text-xs text-slate-500">
                              {productForm.image_urls?.length || 0} image(s) added
                            </p>
                          </div>
                        </div>
                      </div>

                      <div>
                        <label className="block text-sm font-medium mb-2">Description</label>
                        <Textarea
                          value={productForm.description}
                          onChange={(e) => setProductForm({...productForm, description: e.target.value})}
                          placeholder="Detailed product description..."
                          rows={3}
                          required
                        />
                      </div>

                      {/* Specifications */}
                      <div>
                        <h4 className="font-medium text-slate-700 mb-3">Specifications</h4>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                          {productForm.product_type.includes('Printer') && (
                            <>
                              <div>
                                <label className="block text-sm font-medium mb-1">Build Volume</label>
                                <Input
                                  value={productForm.specifications.build_volume}
                                  onChange={(e) => setProductForm({
                                    ...productForm, 
                                    specifications: {...productForm.specifications, build_volume: e.target.value}
                                  })}
                                  placeholder="256 x 256 x 256 mm"
                                />
                              </div>
                              <div>
                                <label className="block text-sm font-medium mb-1">Layer Height</label>
                                <Input
                                  value={productForm.specifications.layer_height}
                                  onChange={(e) => setProductForm({
                                    ...productForm, 
                                    specifications: {...productForm.specifications, layer_height: e.target.value}
                                  })}
                                  placeholder="0.05 - 0.3 mm"
                                />
                              </div>
                              <div>
                                <label className="block text-sm font-medium mb-1">Print Speed</label>
                                <Input
                                  value={productForm.specifications.print_speed}
                                  onChange={(e) => setProductForm({
                                    ...productForm, 
                                    specifications: {...productForm.specifications, print_speed: e.target.value}
                                  })}
                                  placeholder="500 mm/s"
                                />
                              </div>
                              <div>
                                <label className="block text-sm font-medium mb-1">Connectivity</label>
                                <Input
                                  value={productForm.specifications.connectivity}
                                  onChange={(e) => setProductForm({
                                    ...productForm, 
                                    specifications: {...productForm.specifications, connectivity: e.target.value}
                                  })}
                                  placeholder="Wi-Fi, USB, Ethernet"
                                />
                              </div>
                            </>
                          )}

                          {productForm.product_type === 'Filament' && (
                            <>
                              <div>
                                <label className="block text-sm font-medium mb-1">Material Type</label>
                                <Input
                                  value={productForm.specifications.material_compatibility}
                                  onChange={(e) => setProductForm({
                                    ...productForm, 
                                    specifications: {...productForm.specifications, material_compatibility: e.target.value}
                                  })}
                                  placeholder="PLA, ABS, PETG"
                                />
                              </div>
                              <div>
                                <label className="block text-sm font-medium mb-1">Diameter</label>
                                <Input
                                  value={productForm.specifications.diameter}
                                  onChange={(e) => setProductForm({
                                    ...productForm, 
                                    specifications: {...productForm.specifications, diameter: e.target.value}
                                  })}
                                  placeholder="1.75mm"
                                />
                              </div>
                              <div>
                                <label className="block text-sm font-medium mb-1">Available Colors</label>
                                <Input
                                  value={productForm.specifications.color}
                                  onChange={(e) => setProductForm({
                                    ...productForm, 
                                    specifications: {...productForm.specifications, color: e.target.value}
                                  })}
                                  placeholder="Black, White, Red, Blue"
                                />
                              </div>
                              <div>
                                <label className="block text-sm font-medium mb-1">Weight</label>
                                <Input
                                  value={productForm.specifications.weight}
                                  onChange={(e) => setProductForm({
                                    ...productForm, 
                                    specifications: {...productForm.specifications, weight: e.target.value}
                                  })}
                                  placeholder="1kg"
                                />
                              </div>
                            </>
                          )}
                        </div>
                      </div>

                      <div>
                        <label className="block text-sm font-medium mb-2">Tags (comma-separated)</label>
                        <Input
                          value={productForm.tags}
                          onChange={(e) => setProductForm({...productForm, tags: e.target.value})}
                          placeholder="high-speed, enclosed, professional"
                        />
                      </div>

                      <div className="flex space-x-4">
                        <Button type="submit" className="bg-gradient-to-r from-green-500 to-emerald-600 hover:from-green-600 hover:to-emerald-700">
                          {editingProduct ? 'Save Changes' : 'Create Product'}
                        </Button>
                        <Button type="button" onClick={resetProductForm} variant="outline">
                          Cancel
                        </Button>
                      </div>
                    </form>
                  </CardContent>
                </Card>
              )}

              {/* Products List */}
              <Card>
                <CardHeader>
                  <div className="flex items-center justify-between">
                    <div>
                      <CardTitle>Product Inventory ({products.length})</CardTitle>
                      <CardDescription>Manage your product catalog and inventory levels</CardDescription>
                    </div>
                  </div>
                </CardHeader>
                <CardContent>
                  {loading ? (
                    <div className="space-y-4">
                      {[...Array(5)].map((_, i) => (
                        <div key={i} className="animate-pulse flex space-x-4">
                          <div className="w-16 h-16 bg-slate-200 rounded"></div>
                          <div className="flex-1 space-y-2">
                            <div className="h-4 bg-slate-200 rounded"></div>
                            <div className="h-3 bg-slate-200 rounded w-3/4"></div>
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div className="space-y-4">
                      {products.map(product => (
                        <div key={product.id} className="flex items-center space-x-4 p-4 border border-slate-200 rounded-lg hover:shadow-sm transition-shadow">
                          <img
                            src={product.image_url}
                            alt={product.name}
                            className="w-16 h-16 object-cover rounded-lg"
                          />
                          
                          <div className="flex-1">
                            <div className="flex items-start justify-between mb-2">
                              <div>
                                <h4 className="font-medium text-slate-800">{product.name}</h4>
                                <p className="text-sm text-slate-600">{product.manufacturer} • {product.category}</p>
                              </div>
                              <div className="text-right">
                                <div className="font-semibold text-lg">{formatPrice(product.price)}</div>
                                <div className="text-sm text-slate-500">SKU: {product.sku}</div>
                              </div>
                            </div>
                            
                            <div className="flex items-center justify-between">
                              <div className="flex items-center space-x-3 flex-wrap gap-y-1">
                                {getStockStatusBadge(product.stock_quantity)}
                                <span className="text-sm text-slate-600">Stock: {product.stock_quantity}</span>
                                {product.notify_me_enabled && waitlistCounts[product.id] > 0 && (
                                  <button
                                    type="button"
                                    onClick={() => openWaitlistModal(product)}
                                    className="inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-xs font-semibold bg-[#0007b5]/5 text-[#0007b5] border border-[#0007b5]/20 hover:bg-[#0007b5]/10 transition-colors"
                                    title="View customers waiting for this item to be back in stock"
                                    data-testid={`waitlist-badge-${product.id}`}
                                  >
                                    <Bell className="w-3 h-3" />
                                    {waitlistCounts[product.id]} waiting
                                  </button>
                                )}
                                {product.notify_me_enabled && !waitlistCounts[product.id] && (
                                  <span
                                    className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-medium bg-slate-100 text-slate-600 border border-slate-200"
                                    title="Notify Me is enabled for this product"
                                  >
                                    <Bell className="w-3 h-3" />
                                    Notify Me enabled
                                  </span>
                                )}
                                {product.rating && (
                                  <div className="flex items-center space-x-1">
                                    <span className="text-sm">⭐ {product.rating}</span>
                                    <span className="text-sm text-slate-500">({product.review_count})</span>
                                  </div>
                                )}
                              </div>
                              
                              <div className="flex items-center space-x-2">
                                <Button 
                                  size="sm" 
                                  variant="outline"
                                  onClick={() => {
                                    // Navigate to product detail page
                                    window.open(`/products/${product.id}`, '_blank');
                                  }}
                                >
                                  <Eye className="w-4 h-4 mr-1" />
                                  View
                                </Button>
                                <Button 
                                  size="sm" 
                                  variant="outline"
                                  onClick={() => {
                                    try {
                                      // Set editing product and prepare form data
                                      setEditingProduct(product);
                                      
                                      // Properly merge all available images
                                      let allImages = [];
                                      
                                      // Add single image_url if exists
                                      if (product.image_url) {
                                        allImages.push(product.image_url);
                                      }
                                      
                                      // Add images array if exists
                                      if (product.images && Array.isArray(product.images)) {
                                        allImages = allImages.concat(product.images);
                                      }
                                      
                                      // Add image_urls array if exists  
                                      if (product.image_urls && Array.isArray(product.image_urls)) {
                                        allImages = allImages.concat(product.image_urls);
                                      }
                                      
                                      // Remove duplicates
                                      allImages = [...new Set(allImages)];
                                      
                                      const formData = {
                                        name: product.name,
                                        description: product.description,
                                        manufacturer: product.manufacturer,
                                        product_type: product.product_type,
                                        category: product.category,
                                        price: product.price?.toString() || '',
                                        image_urls: allImages,
                                        stock_quantity: product.stock_quantity?.toString() || '',
                                        notify_me_enabled: !!product.notify_me_enabled,
                                        sku: product.sku || '',
                                        specifications: product.specifications || {
                                          build_volume: '',
                                          layer_height: '',
                                          print_speed: '',
                                          connectivity: '',
                                          material_compatibility: '',
                                          diameter: '',
                                          color: '',
                                          weight: ''
                                        },
                                        tags: (product.tags || []).join(', ')
                                      };
                                      
                                      setProductForm(formData);
                                      setShowProductForm(true);
                                      
                                      // UX FIX: Scroll to top so user can see the form
                                      setTimeout(() => {
                                        window.scrollTo({ top: 0, behavior: 'smooth' });
                                      }, 100);
                                      
                                    } catch (error) {
                                      console.error('ERROR in Edit button click handler:', error);
                                      alert(`Error opening edit form: ${error.message}`);
                                    }
                                  }}
                                >
                                  <Edit className="w-4 h-4 mr-1" />
                                  Edit
                                </Button>
                                <Button 
                                  size="sm" 
                                  variant="outline"
                                  onClick={() => openReviewManager(product)}
                                  data-testid={`admin-manage-reviews-btn-${product.id}`}
                                >
                                  <Star className="w-4 h-4 mr-1" />
                                  Reviews
                                </Button>
                                <Button 
                                  size="sm" 
                                  variant="outline" 
                                  className="text-red-600 border-red-200 hover:bg-red-50"
                                  onClick={async () => {
                                    if (window.confirm(`Are you sure you want to delete ${product.name}?`)) {
                                      try {
                                        const token = localStorage.getItem('token');
                                        const response = await fetch(`${process.env.REACT_APP_BACKEND_URL || ''}/api/products/${product.id}`, {
                                          method: 'DELETE',
                                          headers: {
                                            'Authorization': `Bearer ${token}`
                                          }
                                        });
                                        if (response.ok) {
                                          alert('Product deleted successfully');
                                          fetchProducts();
                                        } else {
                                          const error = await response.json();
                                          alert(`Failed to delete product: ${error.detail || 'Unknown error'}`);
                                        }
                                      } catch (error) {
                                        console.error('Delete error:', error);
                                        alert('Failed to delete product. Please try again.');
                                      }
                                    }
                                  }}
                                >
                                  <Trash2 className="w-4 h-4 mr-1" />
                                  Delete
                                </Button>
                              </div>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </CardContent>
              </Card>
            </div>
          </TabsContent>

          {/* Orders Management Tab */}
          <TabsContent value="orders">
            <div className="space-y-6">
              <div>
                <h2 className="text-2xl font-bold text-slate-800">Order Management</h2>
                <p className="text-slate-600">Track and manage customer orders</p>
              </div>

              {/* Lead Time Adjustment Section */}
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center">
                    <Clock className="w-5 h-5 mr-2 text-[#0007b5]" />
                    Order Lead Time Adjustment
                  </CardTitle>
                  <CardDescription>
                    Adjust production time shown to customers for print orders (instant quote and marketplace). 
                    This will add extra time to all displayed production estimates.
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="flex items-end gap-4">
                    <div className="flex-1 max-w-[150px]">
                      <label className="block text-sm font-medium text-slate-700 mb-1">
                        Additional Time
                      </label>
                      <Input
                        type="number"
                        min="0"
                        value={leadTimeAdjustment.value}
                        onChange={(e) => setLeadTimeAdjustment({
                          ...leadTimeAdjustment,
                          value: e.target.value
                        })}
                        placeholder="0"
                      />
                    </div>
                    <div className="flex-1 max-w-[150px]">
                      <label className="block text-sm font-medium text-slate-700 mb-1">
                        Unit
                      </label>
                      <Select
                        value={leadTimeAdjustment.unit}
                        onValueChange={(value) => setLeadTimeAdjustment({
                          ...leadTimeAdjustment,
                          unit: value
                        })}
                      >
                        <SelectTrigger>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="days">Days</SelectItem>
                          <SelectItem value="weeks">Weeks</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <Button 
                      onClick={saveLeadTimeAdjustment}
                      disabled={savingLeadTime}
                      className="bg-[#0007b5] hover:bg-[#000690]"
                    >
                      {savingLeadTime ? 'Saving...' : 'Save'}
                    </Button>
                  </div>
                  {parseInt(leadTimeAdjustment.value) > 0 && (
                    <div className="mt-4 p-3 bg-amber-50 border border-amber-200 rounded-lg">
                      <p className="text-sm text-amber-800">
                        <strong>Active Adjustment:</strong> All production times will show an additional {leadTimeAdjustment.value} {leadTimeAdjustment.unit} to customers.
                        A notice about extended lead times will be displayed in the quote tool.
                      </p>
                    </div>
                  )}
                </CardContent>
              </Card>

              {/* Discount Settings Card */}
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center">
                    <Percent className="w-5 h-5 mr-2 text-[#0007b5]" />
                    Print Quote Discounts
                  </CardTitle>
                  <CardDescription>
                    Apply discounts to printing costs for instant quotes and marketplace print orders. 
                    Does not apply to license fees or shop orders.
                  </CardDescription>
                </CardHeader>
                <CardContent className="space-y-6">
                  {/* Hidden Discount */}
                  <div className="p-4 bg-slate-50 rounded-lg border border-slate-200">
                    <div className="flex items-center gap-2 mb-3">
                      <EyeOff className="w-4 h-4 text-slate-500" />
                      <label className="font-medium text-slate-700">Hidden Discount</label>
                    </div>
                    <p className="text-sm text-slate-500 mb-3">
                      This discount is applied to printing costs but NOT shown to customers.
                    </p>
                    <div className="flex items-center gap-2">
                      <Input
                        type="number"
                        min="0"
                        max="100"
                        step="0.1"
                        value={discountSettings.hiddenDiscount}
                        onChange={(e) => setDiscountSettings({
                          ...discountSettings,
                          hiddenDiscount: e.target.value
                        })}
                        placeholder="0"
                        className="w-24"
                      />
                      <span className="text-slate-600">%</span>
                    </div>
                  </div>

                  {/* Visible Discount */}
                  <div className="p-4 bg-green-50 rounded-lg border border-green-200">
                    <div className="flex items-center gap-2 mb-3">
                      <Eye className="w-4 h-4 text-green-600" />
                      <label className="font-medium text-green-700">Visible Discount</label>
                    </div>
                    <p className="text-sm text-green-600 mb-3">
                      This discount is applied to printing costs AND shown to customers as a line item in the quote.
                    </p>
                    <div className="space-y-3">
                      <div className="flex items-center gap-2">
                        <Input
                          type="number"
                          min="0"
                          max="100"
                          step="0.1"
                          value={discountSettings.visibleDiscount}
                          onChange={(e) => setDiscountSettings({
                            ...discountSettings,
                            visibleDiscount: e.target.value
                          })}
                          placeholder="0"
                          className="w-24"
                        />
                        <span className="text-slate-600">%</span>
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-green-700 mb-1">
                          Discount Name (shown to customers)
                        </label>
                        <Input
                          type="text"
                          value={discountSettings.visibleDiscountName}
                          onChange={(e) => setDiscountSettings({
                            ...discountSettings,
                            visibleDiscountName: e.target.value
                          })}
                          placeholder="Promotional Pricing"
                          className="max-w-xs"
                        />
                      </div>
                    </div>
                  </div>

                  <Button 
                    onClick={saveDiscountSettings}
                    disabled={savingDiscount}
                    className="bg-[#0007b5] hover:bg-[#000690]"
                  >
                    {savingDiscount ? 'Saving...' : 'Save Discount Settings'}
                  </Button>

                  {/* Active Discounts Summary */}
                  {(parseFloat(discountSettings.hiddenDiscount) > 0 || parseFloat(discountSettings.visibleDiscount) > 0) && (
                    <div className="mt-4 p-3 bg-blue-50 border border-blue-200 rounded-lg">
                      <p className="text-sm text-blue-800 font-medium mb-2">Active Discounts:</p>
                      <ul className="text-sm text-blue-700 space-y-1">
                        {parseFloat(discountSettings.hiddenDiscount) > 0 && (
                          <li>• Hidden discount: {discountSettings.hiddenDiscount}% (not shown to customers)</li>
                        )}
                        {parseFloat(discountSettings.visibleDiscount) > 0 && (
                          <li>• "{discountSettings.visibleDiscountName}": {discountSettings.visibleDiscount}% (shown to customers)</li>
                        )}
                      </ul>
                    </div>
                  )}
                </CardContent>
              </Card>

              <Card>
                <CardHeader>
                  <CardTitle>Recent Orders ({orders.length})</CardTitle>
                  <CardDescription>Customer orders from both services and e-commerce</CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="space-y-4">
                    {orders.map(order => (
                      <div key={order.id} className="border border-slate-200 rounded-lg p-4">
                        <div className="flex items-start justify-between mb-3">
                          <div className="flex-1">
                            <div className="font-medium text-slate-800">Order #{order.id.slice(0, 8)}</div>
                            <div className="text-sm text-slate-600">{order.filename}</div>
                            <div className="text-sm text-slate-500">{formatDate(order.created_at)}</div>
                            {order.tracking_number && (
                              <div className="text-sm text-blue-600 mt-1">
                                📦 Tracking: {order.tracking_number}
                              </div>
                            )}
                          </div>
                          <div className="text-right space-y-2">
                            <div className="font-semibold text-lg">{formatPrice(order.estimated_price)}</div>
                            {getOrderStatusBadge(order.status)}
                            <Button 
                              size="sm" 
                              onClick={() => openOrderUpdate(order)}
                              className="w-full"
                            >
                              Update Order
                            </Button>
                          </div>
                        </div>
                        
                        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-sm">
                          <div>
                            <span className="font-medium">Material:</span> {order.material}
                          </div>
                          <div>
                            <span className="font-medium">Quality:</span> {order.quality}
                          </div>
                          <div>
                            <span className="font-medium">Delivery:</span> {order.delivery_weeks} weeks
                          </div>
                        </div>

                        {order.customer_notes && (
                          <div className="mt-3 p-3 bg-slate-50 rounded-lg">
                            <span className="font-medium text-sm">Notes:</span> {order.customer_notes}
                          </div>
                        )}
                        
                        {order.latest_note && (
                          <div className="mt-2 p-2 bg-blue-50 rounded text-sm">
                            <span className="font-medium">Latest Update:</span> {order.latest_note}
                          </div>
                        )}
                      </div>
                    ))}

                    {orders.length === 0 && (
                      <div className="text-center py-8">
                        <ShoppingCart className="w-12 h-12 text-slate-400 mx-auto mb-4" />
                        <p className="text-slate-600">No orders found</p>
                      </div>
                    )}
                  </div>
                </CardContent>
              </Card>
              
              {/* Order Update Modal */}
              {showOrderUpdateModal && selectedOrder && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
                  <div className="bg-white rounded-lg max-w-2xl w-full max-h-[90vh] overflow-y-auto">
                    <div className="p-6">
                      <div className="flex items-center justify-between mb-4">
                        <h3 className="text-xl font-bold">Update Order #{selectedOrder.id.slice(0, 8)}</h3>
                        <Button variant="outline" size="sm" onClick={() => setShowOrderUpdateModal(false)}>
                          Close
                        </Button>
                      </div>
                      
                      <div className="space-y-4">
                        {/* Status Update */}
                        <div>
                          <label className="block text-sm font-medium mb-2">Order Status</label>
                          <Select 
                            value={orderUpdateForm.status} 
                            onValueChange={(value) => setOrderUpdateForm({...orderUpdateForm, status: value})}
                          >
                            <SelectTrigger>
                              <SelectValue placeholder="Select status" />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="pending">Pending</SelectItem>
                              <SelectItem value="confirmed">Confirmed</SelectItem>
                              <SelectItem value="in_production">In Production</SelectItem>
                              <SelectItem value="quality_check">Quality Check</SelectItem>
                              <SelectItem value="shipping">Shipping</SelectItem>
                              <SelectItem value="completed">Completed</SelectItem>
                              <SelectItem value="cancelled">Cancelled</SelectItem>
                            </SelectContent>
                          </Select>
                        </div>
                        
                        {/* Tracking Information */}
                        <div className="grid grid-cols-2 gap-4">
                          <div>
                            <label className="block text-sm font-medium mb-2">Tracking Number</label>
                            <Input
                              value={orderUpdateForm.tracking_number}
                              onChange={(e) => setOrderUpdateForm({...orderUpdateForm, tracking_number: e.target.value})}
                              placeholder="Enter tracking number"
                            />
                          </div>
                          <div>
                            <label className="block text-sm font-medium mb-2">Carrier</label>
                            <Input
                              value={orderUpdateForm.tracking_carrier}
                              onChange={(e) => setOrderUpdateForm({...orderUpdateForm, tracking_carrier: e.target.value})}
                              placeholder="e.g., USPS, UPS, FedEx"
                            />
                          </div>
                        </div>
                        
                        {/* Customer Note */}
                        <div>
                          <label className="block text-sm font-medium mb-2">Customer Note (Visible to Customer)</label>
                          <Textarea
                            value={orderUpdateForm.note}
                            onChange={(e) => setOrderUpdateForm({...orderUpdateForm, note: e.target.value})}
                            placeholder="Add a note for the customer..."
                            rows={3}
                          />
                        </div>
                        
                        {/* Internal Note */}
                        <div>
                          <label className="block text-sm font-medium mb-2">Internal Note (Staff Only)</label>
                          <Textarea
                            value={orderUpdateForm.internal_note}
                            onChange={(e) => setOrderUpdateForm({...orderUpdateForm, internal_note: e.target.value})}
                            placeholder="Add internal notes..."
                            rows={2}
                          />
                        </div>
                        
                        {/* Notify Customer */}
                        <div className="flex items-center space-x-2">
                          <input
                            type="checkbox"
                            checked={orderUpdateForm.notify_customer}
                            onChange={(e) => setOrderUpdateForm({...orderUpdateForm, notify_customer: e.target.checked})}
                            id="notify-customer"
                          />
                          <label htmlFor="notify-customer" className="text-sm">
                            Send email notification to customer
                          </label>
                        </div>
                        
                        {/* Submit Button */}
                        <div className="flex space-x-2">
                          <Button onClick={submitOrderUpdate} className="flex-1">
                            Update Order
                          </Button>
                          <Button variant="outline" onClick={() => setShowOrderUpdateModal(false)}>
                            Cancel
                          </Button>
                        </div>
                        
                        {/* Order History */}
                        {orderHistory.length > 0 && (
                          <div className="mt-6 pt-6 border-t">
                            <h4 className="font-semibold mb-3">Update History</h4>
                            <div className="space-y-2">
                              {orderHistory.map((entry, index) => (
                                <div key={index} className="text-sm p-3 bg-slate-50 rounded">
                                  <div className="font-medium">{new Date(entry.timestamp).toLocaleString()}</div>
                                  <div className="text-slate-600">By: {entry.updated_by}</div>
                                  {entry.changes && Object.keys(entry.changes).length > 0 && (
                                    <div className="mt-1 text-slate-700">
                                      Changes: {JSON.stringify(entry.changes)}
                                    </div>
                                  )}
                                  {entry.customer_note && (
                                    <div className="mt-1 text-blue-700">Note: {entry.customer_note}</div>
                                  )}
                                  {entry.internal_note && (
                                    <div className="mt-1 text-orange-700">Internal: {entry.internal_note}</div>
                                  )}
                                </div>
                              ))}
                            </div>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </TabsContent>

          {/* Marketplace Management Tab */}
          <TabsContent value="marketplace">
            <div className="space-y-6">
              <div>
                <div className="flex items-center justify-between mb-4">
                  <div>
                    <h2 className="text-2xl font-bold text-slate-800">Marketplace Management</h2>
                    <p className="text-slate-600">Manage designer applications and marketplace listings</p>
                  </div>
                  <Button 
                    onClick={loadMarketplaceStats}
                    variant="outline"
                    size="sm"
                    className="flex items-center space-x-2"
                  >
                    <BarChart3 className="w-4 h-4" />
                    <span>Refresh Stats</span>
                  </Button>
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
                <Card>
                  <CardContent className="p-6">
                    <div className="flex items-center space-x-4">
                      <div className="w-12 h-12 bg-purple-100 rounded-lg flex items-center justify-center">
                        <Users className="w-6 h-6 text-purple-600" />
                      </div>
                      <div>
                        <p className="text-sm font-medium text-slate-600">Pending Applications</p>
                        <p className="text-2xl font-bold text-slate-800">{marketplaceStats.applications}</p>
                      </div>
                    </div>
                  </CardContent>
                </Card>

                <Card>
                  <CardContent className="p-6">
                    <div className="flex items-center space-x-4">
                      <div className="w-12 h-12 bg-green-100 rounded-lg flex items-center justify-center">
                        <Package className="w-6 h-6 text-green-600" />
                      </div>
                      <div>
                        <p className="text-sm font-medium text-slate-600">Active Listings</p>
                        <p className="text-2xl font-bold text-slate-800">{marketplaceStats.activeListings}</p>
                      </div>
                    </div>
                  </CardContent>
                </Card>

                <Card>
                  <CardContent className="p-6">
                    <div className="flex items-center space-x-4">
                      <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center">
                        <BarChart3 className="w-6 h-6 text-blue-600" />
                      </div>
                      <div>
                        <p className="text-sm font-medium text-slate-600">Total Sales</p>
                        <p className="text-2xl font-bold text-slate-800">${marketplaceStats.totalSales.toFixed(2)}</p>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </div>

              <Card>
                <CardHeader>
                  <CardTitle>Marketplace Controls</CardTitle>
                  <CardDescription>
                    Quick access to marketplace management features
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                    <Button 
                      onClick={() => {
                        setMarketplaceView('overview');
                      }}
                      variant={marketplaceView === 'overview' ? 'default' : 'outline'}
                      className="flex items-center space-x-2"
                    >
                      <BarChart3 className="w-4 h-4" />
                      <span>Overview</span>
                    </Button>
                    
                    <Button 
                      onClick={() => {
                        setMarketplaceView('listings');
                        loadAllListings();
                      }}
                      variant={marketplaceView === 'listings' ? 'default' : 'outline'}
                      className="flex items-center space-x-2"
                    >
                      <Package className="w-4 h-4" />
                      <span>All Listings</span>
                    </Button>

                    <Button 
                      onClick={() => {
                        setMarketplaceView('designers');
                        loadAllDesigners();
                      }}
                      variant={marketplaceView === 'designers' ? 'default' : 'outline'}
                      className="flex items-center space-x-2"
                    >
                      <Users className="w-4 h-4" />
                      <span>All Designers</span>
                    </Button>
                    
                    <Button 
                      onClick={() => window.open('/marketplace', '_blank')}
                      variant="outline"
                      className="flex items-center space-x-2"
                    >
                      <Eye className="w-4 h-4" />
                      <span>View Public</span>
                    </Button>
                  </div>
                  
                  <div className="mt-6 p-4 bg-blue-50 rounded-lg">
                    <h4 className="font-semibold text-blue-900 mb-2">Revenue Sharing</h4>
                    <div className="grid grid-cols-2 gap-4 text-sm">
                      <div>
                        <span className="text-blue-700">Downloads:</span>
                        <span className="font-semibold ml-2">30% Platform / 70% Designer</span>
                      </div>
                      <div>
                        <span className="text-blue-700">Print Orders:</span>
                        <span className="font-semibold ml-2">80% Platform / 20% Designer</span>
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* Pending Listings Management */}
              <Card>
                <CardHeader>
                  <CardTitle>Draft Listings (Pending Approval)</CardTitle>
                  <CardDescription>
                    Review and approve new marketplace submissions
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  {pendingListings.length > 0 ? (
                    <div className="space-y-4">
                      {pendingListings.map((listing) => (
                        <div key={listing.id} className="border rounded-lg p-4 bg-yellow-50 border-yellow-200">
                          <div className="flex justify-between items-start">
                            <div>
                              <h4 className="font-semibold text-slate-800">{listing.title}</h4>
                              <p className="text-sm text-slate-600">by {listing.designer_name}</p>
                              <p className="text-sm text-slate-500 mt-1">{listing.description}</p>
                              <div className="flex items-center space-x-4 mt-2 text-sm">
                                <span className="text-green-600">Personal: ${listing.personal_price}</span>
                                <span className="text-blue-600">Commercial: ${listing.commercial_price}</span>
                                <Badge variant="outline" className="text-yellow-700 border-yellow-300">
                                  {listing.category}
                                </Badge>
                              </div>
                            </div>
                            <div className="flex space-x-2">
                              <Button 
                                size="sm" 
                                className="bg-green-600 hover:bg-green-700"
                                onClick={() => handleApproveListing(listing.id)}
                              >
                                Approve
                              </Button>
                              <Button 
                                size="sm" 
                                variant="outline" 
                                className="text-red-600 border-red-300 hover:bg-red-50"
                                onClick={() => handleRejectListing(listing.id)}
                              >
                                Reject
                              </Button>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div className="text-center py-8">
                      <Package className="w-12 h-12 text-slate-400 mx-auto mb-4" />
                      <p className="text-slate-600">No draft listings at this time</p>
                      <p className="text-sm text-slate-500 mt-2">New designer submissions will appear here for review</p>
                    </div>
                  )}
                </CardContent>
              </Card>

              {/* Pending Designer Applications - Only show applications needing review */}
              <Card>
                <CardHeader>
                  <div className="flex items-center justify-between">
                    <div>
                      <CardTitle className="flex items-center gap-2">
                        Pending Designer Applications
                        {recentApplications.filter(app => app.status === 'pending').length > 0 && (
                          <Badge variant="destructive" className="text-xs">
                            {recentApplications.filter(app => app.status === 'pending').length} New
                          </Badge>
                        )}
                      </CardTitle>
                      <CardDescription>
                        Applications awaiting your review
                      </CardDescription>
                    </div>
                  </div>
                </CardHeader>
                <CardContent>
                  {recentApplications.filter(app => app.status === 'pending').length > 0 ? (
                    <div className="space-y-4">
                      {recentApplications
                        .filter(app => app.status === 'pending')
                        .map((application) => (
                        <div key={application.id} className="border rounded-lg p-4 bg-yellow-50 border-yellow-200">
                          <div className="flex justify-between items-start">
                            <div className="flex-1">
                              <div className="flex items-center space-x-2">
                                <h4 className="font-semibold text-slate-800">{application.designer_name}</h4>
                                <Badge className="bg-yellow-100 text-yellow-800 text-xs">
                                  <Clock className="w-3 h-3 mr-1" />
                                  Pending Review
                                </Badge>
                              </div>
                              <p className="text-sm text-slate-600">{application.user_email}</p>
                              <p className="text-sm text-slate-500 mt-1 line-clamp-2">{application.bio}</p>
                              {application.portfolio_links && application.portfolio_links.length > 0 && (
                                <div className="mt-2">
                                  <p className="text-xs text-slate-500">Portfolio:</p>
                                  {application.portfolio_links.slice(0, 2).map((link, idx) => (
                                    <a key={idx} href={link} target="_blank" rel="noopener noreferrer" 
                                       className="text-xs text-blue-600 hover:underline block truncate">
                                      {link}
                                    </a>
                                  ))}
                                </div>
                              )}
                              <p className="text-xs text-slate-400 mt-2">
                                Applied: {new Date(application.created_at).toLocaleDateString()}
                              </p>
                            </div>
                            <div className="flex space-x-2 ml-4">
                              <Button 
                                size="sm" 
                                className="bg-green-600 hover:bg-green-700"
                                onClick={() => handleApproveApplication(application.id)}
                              >
                                <CheckCircle className="w-4 h-4 mr-1" />
                                Approve
                              </Button>
                              <Button 
                                size="sm" 
                                variant="outline" 
                                className="text-red-600 border-red-300 hover:bg-red-50"
                                onClick={() => handleRejectApplication(application.id)}
                              >
                                <XCircle className="w-4 h-4 mr-1" />
                                Reject
                              </Button>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div className="text-center py-8">
                      <CheckCircle className="w-12 h-12 text-green-400 mx-auto mb-4" />
                      <p className="text-slate-600">All caught up!</p>
                      <p className="text-sm text-slate-500 mt-2">No pending applications to review</p>
                    </div>
                  )}
                </CardContent>
              </Card>
              
              {/* ALL LISTINGS VIEW */}
              {marketplaceView === 'listings' && (
                <Card>
                  <CardHeader>
                    <CardTitle>All Marketplace Listings</CardTitle>
                    <CardDescription>View and manage all CAD listings</CardDescription>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-3">
                      {allListings.map((listing) => (
                        <div key={listing.id} className="border rounded-lg p-4">
                          <div className="flex items-start justify-between">
                            <div className="flex-1">
                              <h4 className="font-semibold">{listing.title}</h4>
                              <p className="text-sm text-slate-600 mt-1">{listing.description?.substring(0, 100)}...</p>
                              <div className="flex items-center space-x-3 mt-2 text-xs text-slate-500">
                                <span>Designer: {listing.designer_display_name || listing.designer_id?.substring(0, 8)}</span>
                                <span>•</span>
                                <span>Personal: ${listing.personal_price}</span>
                                <span>•</span>
                                <span>Commercial: ${listing.commercial_price}</span>
                                <Badge variant={listing.status === 'active' ? 'default' : 'outline'}>
                                  {listing.status}
                                </Badge>
                              </div>
                            </div>
                            <div className="flex flex-col space-y-1">
                              <Button size="sm" variant="outline" onClick={() => window.open(`/marketplace/listing/${listing.id}`, '_blank')}>
                                <Eye className="w-3 h-3 mr-1" /> View
                              </Button>
                              <Button size="sm" variant="destructive" onClick={() => deleteListing(listing.id)}>
                                <Trash2 className="w-3 h-3 mr-1" /> Delete
                              </Button>
                            </div>
                          </div>
                        </div>
                      ))}
                      {allListings.length === 0 && (
                        <p className="text-center text-slate-500 py-8">No listings found</p>
                      )}
                    </div>
                  </CardContent>
                </Card>
              )}

              {/* ALL DESIGNERS VIEW */}
              {marketplaceView === 'designers' && (
                <Card>
                  <CardHeader>
                    <CardTitle>All Designers</CardTitle>
                    <CardDescription>Manage designer accounts and statuses</CardDescription>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-3">
                      {allDesigners.map((designer) => (
                        <div key={designer.id} className={`border rounded-lg p-4 ${
                          designer.is_suspended 
                            ? 'bg-red-50 border-red-200' 
                            : 'bg-white border-slate-200'
                        }`}>
                          <div className="flex items-start justify-between">
                            <div className="flex-1">
                              <div className="flex items-center gap-2">
                                <h4 className="font-semibold">{designer.name}</h4>
                                {/* Status Badge */}
                                {designer.is_suspended ? (
                                  <Badge variant="destructive" className="text-xs">
                                    <XCircle className="w-3 h-3 mr-1" />
                                    Suspended
                                  </Badge>
                                ) : (
                                  <Badge className="bg-green-100 text-green-800 text-xs">
                                    <CheckCircle className="w-3 h-3 mr-1" />
                                    Active
                                  </Badge>
                                )}
                              </div>
                              <p className="text-sm text-slate-600">{designer.email}</p>
                              <div className="flex items-center flex-wrap gap-2 mt-2">
                                {designer.designer_display_name && (
                                  <Badge variant="outline" className="text-xs">
                                    Display: {designer.designer_display_name}
                                  </Badge>
                                )}
                                {designer.total_listings > 0 && (
                                  <Badge variant="secondary" className="text-xs">
                                    {designer.total_listings} listing{designer.total_listings !== 1 ? 's' : ''}
                                  </Badge>
                                )}
                                {designer.total_sales > 0 && (
                                  <Badge variant="secondary" className="text-xs">
                                    {designer.total_sales} sale{designer.total_sales !== 1 ? 's' : ''}
                                  </Badge>
                                )}
                              </div>
                              {designer.is_suspended && designer.suspended_reason && (
                                <p className="text-xs text-red-600 mt-2">
                                  Reason: {designer.suspended_reason}
                                </p>
                              )}
                            </div>
                            <div className="flex flex-col space-y-2 ml-4">
                              {/* Status Toggle Button */}
                              {designer.is_suspended ? (
                                <Button 
                                  size="sm" 
                                  className="bg-green-600 hover:bg-green-700"
                                  onClick={() => unsuspendDesigner(designer.id)}
                                >
                                  <CheckCircle className="w-4 h-4 mr-1" />
                                  Reactivate
                                </Button>
                              ) : (
                                <Button 
                                  size="sm" 
                                  variant="outline"
                                  className="text-yellow-700 border-yellow-300 hover:bg-yellow-50"
                                  onClick={() => suspendDesigner(designer.id)}
                                >
                                  <Clock className="w-4 h-4 mr-1" />
                                  Freeze
                                </Button>
                              )}
                              {/* Remove Designer Button */}
                              <Button 
                                size="sm" 
                                variant="destructive"
                                onClick={() => removeDesignerStatus(designer.id)}
                              >
                                <Trash2 className="w-4 h-4 mr-1" />
                                Remove
                              </Button>
                            </div>
                          </div>
                        </div>
                      ))}
                      {allDesigners.length === 0 && (
                        <div className="text-center py-8">
                          <Users className="w-12 h-12 text-slate-400 mx-auto mb-4" />
                          <p className="text-slate-500">No designers found</p>
                          <p className="text-sm text-slate-400 mt-1">Approved designers will appear here</p>
                        </div>
                      )}
                    </div>
                  </CardContent>
                </Card>
              )}
            </div>
          </TabsContent>



          {/* Contact Messages Tab */}
          <TabsContent value="contact">
            <div className="space-y-6">
              <div>
                <h2 className="text-2xl font-bold text-slate-800">Contact Form Submissions</h2>
                <p className="text-slate-600">Messages from customers via the contact form</p>
              </div>

              {contactSubmissions.length === 0 ? (
                <Card>
                  <CardContent className="p-12 text-center">
                    <Mail className="w-12 h-12 text-slate-300 mx-auto mb-4" />
                    <p className="text-slate-500">No contact form submissions yet</p>
                  </CardContent>
                </Card>
              ) : (
                <div className="space-y-4">
                  {contactSubmissions.map((submission) => (
                    <Card key={submission.id} className={submission.read ? 'opacity-75' : 'border-blue-200'}>
                      <CardHeader>
                        <div className="flex items-start justify-between">
                          <div className="flex-1">
                            <CardTitle className="text-lg flex items-center gap-2">
                              {!submission.read && (
                                <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                                  New
                                </span>
                              )}
                              {submission.subject}
                            </CardTitle>
                            <CardDescription className="mt-2">
                              From: <strong>{submission.name}</strong> ({submission.email})
                              <br />
                              Date: {new Date(submission.submitted_at).toLocaleString()}
                            </CardDescription>
                          </div>
                          {!submission.read && (
                            <Button
                              onClick={() => markContactAsRead(submission.id)}
                              variant="outline"
                              size="sm"
                            >
                              Mark as Read
                            </Button>
                          )}
                        </div>
                      </CardHeader>
                      <CardContent>
                        <div className="bg-slate-50 p-4 rounded-lg">
                          <p className="text-slate-700 whitespace-pre-wrap">{submission.message}</p>
                        </div>
                        <div className="mt-4">
                          <a
                            href={`mailto:${submission.email}?subject=Re: ${submission.subject}`}
                            className="inline-flex items-center text-blue-600 hover:text-blue-700"
                          >
                            <Mail className="w-4 h-4 mr-2" />
                            Reply via Email
                          </a>
                        </div>
                      </CardContent>
                    </Card>
                  ))}
                </div>
              )}
            </div>
          </TabsContent>

          {/* Analytics Tab */}
          <TabsContent value="analytics">
            <div className="space-y-6">
              <div>
                <h2 className="text-2xl font-bold text-slate-800">Analytics Dashboard</h2>
                <p className="text-slate-600">Business insights and performance metrics</p>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
                <Card>
                  <CardContent className="p-6">
                    <div className="flex items-center space-x-4">
                      <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center">
                        <Package className="w-6 h-6 text-blue-600" />
                      </div>
                      <div>
                        <p className="text-sm font-medium text-slate-600">Total Products</p>
                        <p className="text-2xl font-bold text-slate-800">{products.length}</p>
                      </div>
                    </div>
                  </CardContent>
                </Card>

                <Card>
                  <CardContent className="p-6">
                    <div className="flex items-center space-x-4">
                      <div className="w-12 h-12 bg-green-100 rounded-lg flex items-center justify-center">
                        <ShoppingCart className="w-6 h-6 text-green-600" />
                      </div>
                      <div>
                        <p className="text-sm font-medium text-slate-600">Total Orders</p>
                        <p className="text-2xl font-bold text-slate-800">{orders.length}</p>
                      </div>
                    </div>
                  </CardContent>
                </Card>

                <Card>
                  <CardContent className="p-6">
                    <div className="flex items-center space-x-4">
                      <div className="w-12 h-12 bg-purple-100 rounded-lg flex items-center justify-center">
                        <BarChart3 className="w-6 h-6 text-purple-600" />
                      </div>
                      <div>
                        <p className="text-sm font-medium text-slate-600">Revenue</p>
                        <p className="text-2xl font-bold text-slate-800">
                          {formatPrice(orders.reduce((total, order) => total + (order.final_price || order.estimated_price), 0))}
                        </p>
                      </div>
                    </div>
                  </CardContent>
                </Card>

                <Card>
                  <CardContent className="p-6">
                    <div className="flex items-center space-x-4">
                      <div className="w-12 h-12 bg-orange-100 rounded-lg flex items-center justify-center">
                        <Users className="w-6 h-6 text-orange-600" />
                      </div>
                      <div>
                        <p className="text-sm font-medium text-slate-600">Low Stock Items</p>
                        <p className="text-2xl font-bold text-slate-800">
                          {products.filter(p => p.stock_quantity < 5).length}
                        </p>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </div>

              {/* Low Stock Alert */}
              {products.filter(p => p.stock_quantity < 5).length > 0 && (
                <Card className="border-orange-200 bg-orange-50">
                  <CardHeader>
                    <CardTitle className="text-orange-800">⚠️ Low Stock Alert</CardTitle>
                    <CardDescription>The following products need restocking</CardDescription>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-2">
                      {products
                        .filter(p => p.stock_quantity < 5)
                        .map(product => (
                          <div key={product.id} className="flex justify-between items-center">
                            <span className="font-medium">{product.name}</span>
                            <Badge variant="outline" className="border-orange-500 text-orange-600">
                              {product.stock_quantity} left
                            </Badge>
                          </div>
                        ))}
                    </div>
                  </CardContent>
                </Card>
              )}
            </div>
          </TabsContent>

          {/* Settings Tab */}
          <TabsContent value="settings">
            <div className="space-y-6">
              <div>
                <h2 className="text-2xl font-bold text-slate-800">Admin Settings</h2>
                <p className="text-slate-600">System configuration and management tools</p>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <Card>
                  <CardHeader>
                    <CardTitle>User Management</CardTitle>
                    <CardDescription>Manage user accounts and permissions</CardDescription>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-4">
                      <div className="flex justify-between items-center">
                        <span>Total Users</span>
                        <Badge variant="outline">Coming Soon</Badge>
                      </div>
                      <div className="flex justify-between items-center">
                        <span>Admin Users</span>
                        <Badge variant="outline">1 (You)</Badge>
                      </div>
                    </div>
                  </CardContent>
                </Card>

                <Card>
                  <CardHeader>
                    <CardTitle>System Health</CardTitle>
                    <CardDescription>Monitor system status and performance</CardDescription>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-4">
                      <div className="flex justify-between items-center">
                        <span>Database Connection</span>
                        <Badge variant="outline" className="border-green-500 text-green-600">Online</Badge>
                      </div>
                      <div className="flex justify-between items-center">
                        <span>Payment System</span>
                        <Badge variant="outline" className="border-green-500 text-green-600">Active</Badge>
                      </div>
                      <div className="flex justify-between items-center">
                        <span>3D File Processing</span>
                        <Badge variant="outline" className="border-green-500 text-green-600">Operational</Badge>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </div>
            </div>
          </TabsContent>


          {/* Inventory Management Tab */}
          <TabsContent value="inventory">
            <div className="space-y-6">
              <div className="flex items-center justify-between">
                <div>
                  <h2 className="text-2xl font-bold text-slate-800">Inventory Management</h2>
                  <p className="text-slate-600">Manage product stock levels</p>
                </div>
                <div className="flex space-x-2">
                  <Button variant="outline" onClick={async () => {
                    try {
                      const response = await makeAuthenticatedRequest('/api/admin/inventory/export');
                      if (response.ok) {
                        const blob = await response.blob();
                        const url = window.URL.createObjectURL(blob);
                        const a = document.createElement('a');
                        a.href = url;
                        a.download = 'inventory_export.csv';
                        document.body.appendChild(a);
                        a.click();
                        window.URL.revokeObjectURL(url);
                        document.body.removeChild(a);
                      }
                    } catch (error) {
                      alert('Error exporting inventory');
                    }
                  }}>
                    Export CSV
                  </Button>
                  <Button onClick={() => document.getElementById('inventory-csv-upload').click()}>
                    Bulk Upload CSV
                  </Button>
                  <input
                    id="inventory-csv-upload"
                    type="file"
                    accept=".csv"
                    style={{display: 'none'}}
                    onChange={async (e) => {
                      const file = e.target.files[0];
                      if (!file) return;
                      
                      const formData = new FormData();
                      formData.append('file', file);
                      
                      try {
                        const idToken = await getIdToken();
                        const response = await fetch(
                          `${process.env.REACT_APP_BACKEND_URL || ''}/api/admin/inventory/bulk-upload`,
                          {
                            method: 'POST',
                            headers: {
                              'Authorization': `Bearer ${idToken}`
                            },
                            body: formData
                          }
                        );
                        
                        if (response.ok) {
                          const data = await response.json();
                          alert(`Upload successful! ${data.message}\nErrors: ${data.results.errors.length}`);
                          loadInventory();
                        } else {
                          alert('Error uploading CSV');
                        }
                      } catch (error) {
                        alert('Error uploading CSV');
                      }
                      e.target.value = '';
                    }}
                  />
                </div>
              </div>

              <Card className="bg-blue-50 border-blue-200">
                <CardContent className="pt-6">
                  <p className="text-sm text-blue-800">
                    <strong>CSV Format:</strong> product_id, sku, stock_quantity, low_stock_threshold
                  </p>
                  <p className="text-xs text-blue-600 mt-2">
                    Use product_id OR sku to identify products. Download current inventory to see format.
                  </p>
                </CardContent>
              </Card>

              {showInventoryForm && selectedProductForInventory && (
                <Card>
                  <CardHeader>
                    <CardTitle>Adjust Inventory - {selectedProductForInventory.name}</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-4">
                      <div>
                        <label className="block text-sm font-medium mb-2">Quantity Change</label>
                        <Input
                          type="number"
                          placeholder="Enter positive for restock, negative for removal"
                          value={inventoryAdjustment.quantity_change}
                          onChange={(e) => setInventoryAdjustment({...inventoryAdjustment, quantity_change: e.target.value})}
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium mb-2">Reason</label>
                        <Input
                          placeholder="e.g., New shipment, Damage, Sale"
                          value={inventoryAdjustment.reason}
                          onChange={(e) => setInventoryAdjustment({...inventoryAdjustment, reason: e.target.value})}
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium mb-2">Notes (Optional)</label>
                        <Textarea
                          placeholder="Additional details..."
                          value={inventoryAdjustment.notes}
                          onChange={(e) => setInventoryAdjustment({...inventoryAdjustment, notes: e.target.value})}
                        />
                      </div>
                      <div className="flex space-x-2">
                        <Button onClick={() => handleInventoryAdjustment(selectedProductForInventory.id)}>
                          Save Adjustment
                        </Button>
                        <Button variant="outline" onClick={() => {
                          setShowInventoryForm(false);
                          setSelectedProductForInventory(null);
                          setInventoryAdjustment({ quantity_change: '', reason: '', notes: '' });
                        }}>
                          Cancel
                        </Button>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              )}

              <Card>
                <CardHeader>
                  <CardTitle>Products Inventory</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-4">
                    {products.map((product) => {
                      const inventoryItem = inventory.find(i => i.product_id === product.id);
                      const stockQty = inventoryItem?.stock_quantity || 0;
                      const status = inventoryItem?.status || 'in_stock';
                      
                      return (
                        <div key={product.id} className="flex items-center justify-between border-b pb-4">
                          <div className="flex-1">
                            <h4 className="font-medium">{product.name}</h4>
                            <p className="text-sm text-slate-600">SKU: {product.sku || 'N/A'}</p>
                          </div>
                          <div className="flex items-center space-x-4">
                            <div className="text-center">
                              <p className="text-sm text-slate-600">Stock</p>
                              <p className="text-lg font-bold">{stockQty}</p>
                            </div>
                            <Badge variant={status === 'in_stock' ? 'default' : status === 'low_stock' ? 'warning' : 'destructive'}>
                              {status === 'in_stock' ? 'In Stock' : status === 'low_stock' ? 'Low Stock' : 'Out of Stock'}
                            </Badge>
                            <Button
                              size="sm"
                              onClick={() => {
                                setSelectedProductForInventory(product);
                                setShowInventoryForm(true);
                              }}
                            >
                              Adjust
                            </Button>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </CardContent>
              </Card>
            </div>
          </TabsContent>

          {/* Reviews Management Tab */}
          <TabsContent value="reviews">
            <div className="space-y-6">
              <div className="flex items-center justify-between">
                <div>
                  <h2 className="text-2xl font-bold text-slate-800">Customer Reviews</h2>
                  <p className="text-slate-600">Moderate product and listing reviews</p>
                </div>
                <Select value={reviewFilter} onValueChange={setReviewFilter}>
                  <SelectTrigger className="w-[180px]">
                    <SelectValue placeholder="Filter reviews" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Reviews</SelectItem>
                    <SelectItem value="pending">Pending</SelectItem>
                    <SelectItem value="approved">Approved</SelectItem>
                    <SelectItem value="rejected">Rejected</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <Card>
                <CardHeader>
                  <CardTitle>Reviews</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-4">
                    {reviews
                      .filter(r => reviewFilter === 'all' || r.status === reviewFilter)
                      .map((review) => (
                        <div key={review.id} className="border rounded-lg p-4 space-y-3">
                          <div className="flex items-start justify-between">
                            <div className="flex-1">
                              <div className="flex items-center space-x-2 mb-2">
                                <span className="font-medium">{review.user_name}</span>
                                {review.verified_purchase && (
                                  <Badge variant="outline" className="text-green-600 border-green-600">
                                    Verified Purchase
                                  </Badge>
                                )}
                                <div className="flex">
                                  {[...Array(5)].map((_, i) => (
                                    <Star
                                      key={i}
                                      className={`w-4 h-4 ${i < review.rating ? 'fill-yellow-400 text-yellow-400' : 'text-gray-300'}`}
                                    />
                                  ))}
                                </div>
                              </div>
                              <h4 className="font-semibold text-slate-900">{review.title}</h4>
                              <p className="text-slate-600 mt-1">{review.comment}</p>
                              <p className="text-xs text-slate-400 mt-2">
                                {new Date(review.created_at).toLocaleDateString()} • Helpful: {review.helpful_count}
                              </p>
                              {review.admin_notes && (
                                <div className="mt-2 p-2 bg-slate-100 rounded text-sm">
                                  <strong>Admin Notes:</strong> {review.admin_notes}
                                </div>
                              )}
                            </div>
                            <Badge
                              variant={review.status === 'approved' ? 'default' : review.status === 'pending' ? 'warning' : 'destructive'}
                            >
                              {review.status}
                            </Badge>
                          </div>
                          <div className="flex space-x-2">
                            {review.status !== 'approved' && (
                              <Button size="sm" onClick={() => moderateReview(review.id, 'approved')}>
                                Approve
                              </Button>
                            )}
                            {review.status !== 'rejected' && (
                              <Button size="sm" variant="outline" onClick={() => {
                                const notes = prompt('Rejection reason (optional):');
                                moderateReview(review.id, 'rejected', notes || '');
                              }}>
                                Reject
                              </Button>
                            )}
                            <Button size="sm" variant="destructive" onClick={() => deleteReview(review.id)}>
                              Delete
                            </Button>
                          </div>
                        </div>
                      ))}
                    {reviews.filter(r => reviewFilter === 'all' || r.status === reviewFilter).length === 0 && (
                      <p className="text-center text-slate-500 py-8">No reviews found</p>
                    )}
                  </div>
                </CardContent>
              </Card>
            </div>
          </TabsContent>

          {/* CMS Editor Tab */}
          <TabsContent value="cms">
            <div className="space-y-6">
              <div className="flex items-center justify-between">
                <div>
                  <h2 className="text-2xl font-bold text-slate-800">CMS Editor</h2>
                  <p className="text-slate-600">Edit website content pages</p>
                </div>
                {!showCmsEditor && (
                  <Button onClick={() => {
                    setSelectedPage({ slug: 'new', title: '', content: '', meta_description: '', is_published: true });
                    setCmsForm({ title: '', content: '', meta_description: '', is_published: true });
                    setShowCmsEditor(true);
                  }}>
                    <Plus className="w-4 h-4 mr-2" />
                    Create New Page
                  </Button>
                )}
              </div>

              {showCmsEditor && selectedPage ? (
                <Card>
                  <CardHeader>
                    <CardTitle>{selectedPage.slug === 'new' ? 'Create New Page' : `Edit Page: ${selectedPage.slug}`}</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <form onSubmit={handleCmsSubmit} className="space-y-4">
                      {selectedPage.slug === 'new' && (
                        <div>
                          <label className="block text-sm font-medium mb-2">Page Slug (URL path)</label>
                          <Input
                            placeholder="e.g., privacy-policy"
                            onChange={(e) => setSelectedPage({...selectedPage, slug: e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, '-')})}
                            required
                          />
                          <p className="text-xs text-slate-500 mt-1">Will appear as: /pages/{selectedPage.slug}</p>
                        </div>
                      )}
                      <div>
                        <label className="block text-sm font-medium mb-2">Page Title</label>
                        <Input
                          value={cmsForm.title}
                          onChange={(e) => setCmsForm({...cmsForm, title: e.target.value})}
                          required
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium mb-2">Content (HTML)</label>
                        <Textarea
                          value={cmsForm.content}
                          onChange={(e) => setCmsForm({...cmsForm, content: e.target.value})}
                          rows={15}
                          className="font-mono text-sm"
                          required
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium mb-2">Meta Description</label>
                        <Textarea
                          value={cmsForm.meta_description}
                          onChange={(e) => setCmsForm({...cmsForm, meta_description: e.target.value})}
                          rows={2}
                        />
                      </div>
                      <div className="flex items-center space-x-2">
                        <input
                          type="checkbox"
                          checked={cmsForm.is_published}
                          onChange={(e) => setCmsForm({...cmsForm, is_published: e.target.checked})}
                          id="is_published"
                        />
                        <label htmlFor="is_published" className="text-sm font-medium">
                          Published (visible to public)
                        </label>
                      </div>
                      <div className="flex space-x-2">
                        <Button type="submit">Save Changes</Button>
                        <Button type="button" variant="outline" onClick={() => {
                          setShowCmsEditor(false);
                          setSelectedPage(null);
                        }}>
                          Cancel
                        </Button>
                      </div>
                    </form>
                  </CardContent>
                </Card>
              ) : (
                <Card>
                  <CardHeader>
                    <CardTitle>Website Pages</CardTitle>
                    <CardDescription>Click on a page to edit its content</CardDescription>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-2">
                      {cmsPages.map((page) => (
                        <div
                          key={page.id}
                          className="flex items-center justify-between p-4 border rounded-lg hover:bg-slate-50 cursor-pointer"
                          onClick={() => handleCmsPageSelect(page)}
                        >
                          <div>
                            <h4 className="font-medium">{page.title}</h4>
                            <p className="text-sm text-slate-600">/{page.slug}</p>
                          </div>
                          <div className="flex items-center space-x-2">
                            <Badge variant={page.is_published ? 'default' : 'outline'}>
                              {page.is_published ? 'Published' : 'Draft'}
                            </Badge>
                            <Edit className="w-4 h-4 text-slate-400" />
                          </div>
                        </div>
                      ))}
                      {cmsPages.length === 0 && (
                        <p className="text-center text-slate-500 py-8">No pages found. Create default pages to get started.</p>
                      )}
                    </div>
                  </CardContent>
                </Card>
              )}
            </div>
          </TabsContent>


          {/* User Management Tab */}
          <TabsContent value="users">
            <div className="space-y-6">
              <div className="flex items-center justify-between">
                <div>
                  <h2 className="text-2xl font-bold text-slate-800">User Management</h2>
                  <p className="text-slate-600">Manage user accounts, roles, and permissions</p>
                </div>
                <Button onClick={loadAllUsers}>
                  Refresh Users
                </Button>
              </div>

              <div className="mb-4">
                <Input
                  placeholder="Search users by name or email..."
                  value={userSearchTerm}
                  onChange={(e) => setUserSearchTerm(e.target.value)}
                  className="max-w-md"
                />
              </div>

              <Card>
                <CardHeader>
                  <CardTitle>All Users</CardTitle>
                  <CardDescription>View and manage platform users and their roles</CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="space-y-3">
                    {allUsers
                      .filter(u => 
                        !userSearchTerm || 
                        u.name?.toLowerCase().includes(userSearchTerm.toLowerCase()) ||
                        u.email?.toLowerCase().includes(userSearchTerm.toLowerCase())
                      )
                      .map((user) => (
                        <div key={user.id} className="border rounded-lg p-4">
                          <div className="flex items-start justify-between">
                            <div className="flex-1">
                              <div className="flex items-center space-x-3">
                                <h4 className="font-semibold text-lg">{user.name || 'Unnamed'}</h4>
                                {user.role_info?.role === 'SUPER_ADMIN' && (
                                  <Badge variant="default" className="bg-purple-600">Super Admin</Badge>
                                )}
                                {user.role_info?.role === 'ADMIN' && (
                                  <Badge variant="default" className="bg-blue-600">Admin</Badge>
                                )}
                                {user.role_info?.role === 'MANAGER' && (
                                  <Badge variant="secondary">Manager</Badge>
                                )}
                                {user.role_info?.role === 'STAFF' && (
                                  <Badge variant="outline">Staff</Badge>
                                )}
                              </div>
                              <p className="text-sm text-slate-600 mt-1">{user.email}</p>
                              <div className="flex items-center space-x-2 mt-2 text-xs">
                                {user.is_designer && <Badge className="bg-green-600">Designer</Badge>}
                                {user.is_banned && <Badge variant="destructive">Banned</Badge>}
                                {user.is_suspended && <Badge variant="destructive">Suspended</Badge>}
                                {user.role_info?.assigned_at && (
                                  <span className="text-slate-500">
                                    Role assigned: {new Date(user.role_info.assigned_at).toLocaleDateString()}
                                  </span>
                                )}
                              </div>
                              {user.role_info?.role && (
                                <div className="mt-2 text-xs text-slate-600">
                                  <strong>Permissions:</strong> {getRolePermissions(user.role_info.role)}
                                </div>
                              )}
                            </div>
                            <div className="flex flex-col space-y-2">
                              <Button 
                                size="sm" 
                                variant="outline"
                                onClick={() => {
                                  setSelectedUser(user);
                                  setShowUserEditor(true);
                                }}
                              >
                                <Edit className="w-4 h-4 mr-1" />
                                Manage Roles
                              </Button>
                              {user.is_banned ? (
                                <Button size="sm" onClick={() => unbanUser(user.id)}>
                                  Unban
                                </Button>
                              ) : (
                                <Button size="sm" variant="destructive" onClick={() => banUser(user.id)}>
                                  Ban User
                                </Button>
                              )}
                            </div>
                          </div>
                        </div>
                      ))}
                    {allUsers.filter(u => 
                      !userSearchTerm || 
                      u.name?.toLowerCase().includes(userSearchTerm.toLowerCase()) ||
                      u.email?.toLowerCase().includes(userSearchTerm.toLowerCase())
                    ).length === 0 && (
                      <p className="text-center text-slate-500 py-8">No users found</p>
                    )}
                  </div>
                </CardContent>
              </Card>
            </div>

            {/* User Role Editor Modal */}
            {showUserEditor && selectedUser && (
              <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
                <div className="bg-white rounded-lg max-w-2xl w-full max-h-[90vh] overflow-y-auto">
                  <div className="p-6">
                    <div className="flex items-center justify-between mb-6">
                      <h3 className="text-2xl font-bold">Manage User Role</h3>
                      <Button variant="ghost" size="sm" onClick={() => {
                        setShowUserEditor(false);
                        setSelectedUser(null);
                      }}>
                        ×
                      </Button>
                    </div>

                    <div className="space-y-6">
                      <div className="bg-slate-50 p-4 rounded-lg">
                        <h4 className="font-semibold text-lg mb-2">{selectedUser.name || 'Unnamed User'}</h4>
                        <p className="text-sm text-slate-600">{selectedUser.email}</p>
                        <p className="text-xs text-slate-500 mt-2">
                          User ID: {selectedUser.firebase_uid || selectedUser.id}
                        </p>
                      </div>

                      <div>
                        <label className="block text-sm font-medium mb-3">Assign Role</label>
                        <div className="space-y-3">
                          {/* Super Admin Role */}
                          <div 
                            className={`border-2 rounded-lg p-4 cursor-pointer transition-all ${
                              selectedUser.role_info?.role === 'SUPER_ADMIN' 
                                ? 'border-purple-600 bg-purple-50' 
                                : 'border-slate-200 hover:border-slate-300'
                            }`}
                            onClick={() => assignRoleToUser(selectedUser.email, 'super_admin')}
                          >
                            <div className="flex items-center justify-between">
                              <div>
                                <div className="flex items-center space-x-2">
                                  <h5 className="font-semibold text-purple-600">Super Admin</h5>
                                  <Badge variant="default" className="bg-purple-600">Highest Access</Badge>
                                </div>
                                <p className="text-sm text-slate-600 mt-1">
                                  Full system access including role management, all product and order management
                                </p>
                                <p className="text-xs text-slate-500 mt-2">
                                  ✓ Can assign roles to other users
                                </p>
                              </div>
                              {selectedUser.role_info?.role === 'SUPER_ADMIN' && (
                                <Badge variant="default" className="bg-purple-600">Current</Badge>
                              )}
                            </div>
                          </div>

                          {/* Admin Role */}
                          <div 
                            className={`border-2 rounded-lg p-4 cursor-pointer transition-all ${
                              selectedUser.role_info?.role === 'ADMIN' 
                                ? 'border-blue-600 bg-blue-50' 
                                : 'border-slate-200 hover:border-slate-300'
                            }`}
                            onClick={() => assignRoleToUser(selectedUser.email, 'admin')}
                          >
                            <div className="flex items-center justify-between">
                              <div>
                                <div className="flex items-center space-x-2">
                                  <h5 className="font-semibold text-blue-600">Admin</h5>
                                  <Badge variant="default" className="bg-blue-600">High Access</Badge>
                                </div>
                                <p className="text-sm text-slate-600 mt-1">
                                  Manage products, orders, and view analytics
                                </p>
                                <p className="text-xs text-slate-500 mt-2">
                                  ✗ Cannot assign roles to other users
                                </p>
                              </div>
                              {selectedUser.role_info?.role === 'ADMIN' && (
                                <Badge variant="default" className="bg-blue-600">Current</Badge>
                              )}
                            </div>
                          </div>

                          {/* Manager Role */}
                          <div 
                            className={`border-2 rounded-lg p-4 cursor-pointer transition-all ${
                              selectedUser.role_info?.role === 'MANAGER' 
                                ? 'border-amber-600 bg-amber-50' 
                                : 'border-slate-200 hover:border-slate-300'
                            }`}
                            onClick={() => assignRoleToUser(selectedUser.email, 'manager')}
                          >
                            <div className="flex items-center justify-between">
                              <div>
                                <div className="flex items-center space-x-2">
                                  <h5 className="font-semibold text-amber-600">Manager</h5>
                                  <Badge variant="secondary">Limited Access</Badge>
                                </div>
                                <p className="text-sm text-slate-600 mt-1">
                                  View products, manage orders, and view analytics
                                </p>
                                <p className="text-xs text-slate-500 mt-2">
                                  ✗ Cannot create/edit/delete products
                                </p>
                              </div>
                              {selectedUser.role_info?.role === 'MANAGER' && (
                                <Badge variant="secondary">Current</Badge>
                              )}
                            </div>
                          </div>

                          {/* Staff Role */}
                          <div 
                            className={`border-2 rounded-lg p-4 cursor-pointer transition-all ${
                              selectedUser.role_info?.role === 'STAFF' 
                                ? 'border-slate-600 bg-slate-50' 
                                : 'border-slate-200 hover:border-slate-300'
                            }`}
                            onClick={() => assignRoleToUser(selectedUser.email, 'staff')}
                          >
                            <div className="flex items-center justify-between">
                              <div>
                                <div className="flex items-center space-x-2">
                                  <h5 className="font-semibold text-slate-600">Staff</h5>
                                  <Badge variant="outline">Read-Only</Badge>
                                </div>
                                <p className="text-sm text-slate-600 mt-1">
                                  View products and orders only
                                </p>
                                <p className="text-xs text-slate-500 mt-2">
                                  ✗ No editing or management capabilities
                                </p>
                              </div>
                              {selectedUser.role_info?.role === 'STAFF' && (
                                <Badge variant="outline">Current</Badge>
                              )}
                            </div>
                          </div>

                          {/* Customer (Remove Role) */}
                          <div 
                            className={`border-2 rounded-lg p-4 cursor-pointer transition-all ${
                              !selectedUser.role_info?.role || selectedUser.role_info?.role === 'CUSTOMER'
                                ? 'border-slate-400 bg-slate-50' 
                                : 'border-slate-200 hover:border-slate-300'
                            }`}
                            onClick={() => assignRoleToUser(selectedUser.email, 'customer')}
                          >
                            <div className="flex items-center justify-between">
                              <div>
                                <div className="flex items-center space-x-2">
                                  <h5 className="font-semibold text-slate-600">Customer</h5>
                                  <Badge variant="outline">Default</Badge>
                                </div>
                                <p className="text-sm text-slate-600 mt-1">
                                  Regular user with no admin access
                                </p>
                              </div>
                              {(!selectedUser.role_info?.role || selectedUser.role_info?.role === 'CUSTOMER') && (
                                <Badge variant="outline">Current</Badge>
                              )}
                            </div>
                          </div>
                        </div>
                      </div>

                      <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
                        <p className="text-sm text-amber-800">
                          <strong>⚠️ Note:</strong> Role changes take effect immediately. The user may need to log out and log back in to see their new permissions.
                        </p>
                      </div>

                      <div className="flex justify-end space-x-2">
                        <Button variant="outline" onClick={() => {
                          setShowUserEditor(false);
                          setSelectedUser(null);
                        }}>
                          Close
                        </Button>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            )}
          </TabsContent>

          {/* File Downloads Tab */}
          <TabsContent value="files">
            <div className="space-y-6">
              <div>
                <h2 className="text-2xl font-bold text-slate-800">Service Files & Downloads</h2>
                <p className="text-slate-600">Download CAD files from quotes and service requests</p>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-2">
                <Button onClick={loadQuotes} variant="outline">
                  Load Quote Requests
                </Button>
                <Button onClick={loadServiceRequests} variant="outline">
                  Load Service Requests
                </Button>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
                <Button
                  onClick={exportQuotesCsv}
                  variant="outline"
                  className="text-[#0007b5] border-[#0007b5]/30 hover:bg-[#0007b5]/5"
                  data-testid="admin-export-quotes-csv"
                >
                  <Download className="w-4 h-4 mr-2" />
                  Export Quotes · CSV
                </Button>
                <Button
                  onClick={exportServiceRequestsCsv}
                  variant="outline"
                  className="text-[#0007b5] border-[#0007b5]/30 hover:bg-[#0007b5]/5"
                  data-testid="admin-export-service-requests-csv"
                >
                  <Download className="w-4 h-4 mr-2" />
                  Export Service Requests · CSV
                </Button>
              </div>

              {/* Quote Files */}
              {quotes.length > 0 && (
                <Card>
                  <CardHeader>
                    <CardTitle>Quote Request Files ({quotes.length})</CardTitle>
                    <CardDescription>Click a row to view full details — includes CAD file download.</CardDescription>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-3">
                      {quotes.map((quote) => (
                        <div
                          key={quote.id}
                          role="button"
                          tabIndex={0}
                          onClick={() => setFileDetail({ kind: 'quote', record: quote })}
                          onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); setFileDetail({ kind: 'quote', record: quote }); } }}
                          className="border rounded-lg p-4 cursor-pointer hover:border-[#0007b5] hover:bg-slate-50 transition-colors"
                          data-testid={`admin-quote-row-${quote.id}`}
                        >
                          <div className="flex items-start justify-between gap-3">
                            <div className="flex-1 min-w-0">
                              <h4 className="font-medium truncate">{quote.filename}</h4>
                              <p className="text-sm text-slate-600 mt-1">
                                Email: {quote.contact_email}
                              </p>
                              <p className="text-xs text-slate-500 mt-1">
                                Material: {quote.material} | Quality: {quote.quality} | Price: ${quote.estimated_price}
                              </p>
                              <p className="text-xs text-slate-400 mt-1">
                                {new Date(quote.created_at).toLocaleString()}
                              </p>
                            </div>
                            <div className="flex flex-col items-end space-y-1 shrink-0">
                              {quote.file_available ? (
                                <Button
                                  size="sm"
                                  onClick={(e) => { e.stopPropagation(); downloadQuoteFile(quote.id); }}
                                >
                                  <Download className="w-3 h-3 mr-1" /> Download
                                </Button>
                              ) : (
                                <Badge variant="outline">No File</Badge>
                              )}
                              <span className="text-[10px] text-slate-400 uppercase tracking-wider">View details</span>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </CardContent>
                </Card>
              )}

              {/* Service Request Files */}
              {serviceRequests.length > 0 && (
                <Card>
                  <CardHeader>
                    <CardTitle>Service Request Files ({serviceRequests.length})</CardTitle>
                    <CardDescription>Click a row to view full details and download files.</CardDescription>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-3">
                      {serviceRequests.map((request) => (
                        <div
                          key={request.id}
                          role="button"
                          tabIndex={0}
                          onClick={() => setFileDetail({ kind: 'service', record: request })}
                          onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); setFileDetail({ kind: 'service', record: request }); } }}
                          className="border rounded-lg p-4 cursor-pointer hover:border-[#0007b5] hover:bg-slate-50 transition-colors"
                          data-testid={`admin-service-row-${request.id}`}
                        >
                          <div className="flex items-start justify-between gap-3">
                            <div className="flex-1 min-w-0">
                              <h4 className="font-medium">{request.service_type}</h4>
                              <p className="text-sm text-slate-600 mt-1">
                                From: {request.contact_name} ({request.contact_email})
                              </p>
                              {request.filename && (
                                <p className="text-xs text-slate-500 mt-1 truncate">
                                  File: {request.filename}
                                </p>
                              )}
                              <p className="text-xs text-slate-400 mt-1">
                                {new Date(request.created_at).toLocaleString()}
                              </p>
                            </div>
                            <div className="flex flex-col items-end space-y-1 shrink-0">
                              {request.file_available && request.files_attached ? (
                                <Button
                                  size="sm"
                                  onClick={(e) => { e.stopPropagation(); downloadServiceFile(request.id); }}
                                >
                                  <Download className="w-3 h-3 mr-1" /> Download
                                </Button>
                              ) : (
                                <Badge variant="outline">No File</Badge>
                              )}
                              <span className="text-[10px] text-slate-400 uppercase tracking-wider">View details</span>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </CardContent>
                </Card>
              )}

              {quotes.length === 0 && serviceRequests.length === 0 && (
                <Card>
                  <CardContent className="py-12 text-center">
                    <Package className="w-12 h-12 text-slate-400 mx-auto mb-4" />
                    <p className="text-slate-600">Click the buttons above to load files</p>
                  </CardContent>
                </Card>
              )}

              {/* Detail modal */}
              <Dialog open={!!fileDetail} onOpenChange={(open) => { if (!open) setFileDetail(null); }}>
                <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto bg-white text-slate-900 border border-slate-200 shadow-2xl" data-testid="admin-file-detail-dialog">
                  {fileDetail && (() => {
                    const { kind, record } = fileDetail;
                    const isQuote = kind === 'quote';
                    const title = isQuote
                      ? `Quote Request · ${record.filename || 'Unnamed file'}`
                      : `Service Request · ${record.service_type || 'Service'}`;
                    const canDownload = isQuote
                      ? !!record.file_available
                      : !!(record.file_available && record.files_attached);
                    const onDownload = () => {
                      if (isQuote) downloadQuoteFile(record.id);
                      else downloadServiceFile(record.id);
                    };

                    // Field display helpers
                    const Row = ({ label, value }) => (
                      value === undefined || value === null || value === '' ? null : (
                        <div className="grid grid-cols-[160px_1fr] gap-3 py-2 border-b border-slate-100 last:border-0">
                          <dt className="text-xs uppercase tracking-wide text-slate-500">{label}</dt>
                          <dd className="text-sm text-slate-900 break-words whitespace-pre-wrap">{String(value)}</dd>
                        </div>
                      )
                    );
                    const money = (n) => (typeof n === 'number' ? `$${n.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}` : null);

                    return (
                      <>
                        <DialogHeader>
                          <DialogTitle className="flex items-center gap-2">
                            <FileText className="w-5 h-5 text-[#0007b5]" />
                            {title}
                          </DialogTitle>
                          <DialogDescription>
                            {record.id ? `ID: ${record.id}` : null}
                            {record.created_at ? ` · Submitted ${new Date(record.created_at).toLocaleString()}` : null}
                          </DialogDescription>
                        </DialogHeader>

                        <div className="mt-4 space-y-5">
                          {/* Contact */}
                          <section>
                            <h4 className="text-xs font-semibold uppercase tracking-wider text-slate-500 mb-1">Contact</h4>
                            <dl>
                              <Row label="Name" value={record.contact_name} />
                              <Row label="Email" value={record.contact_email} />
                              <Row label="Phone" value={record.phone} />
                              <Row label="Company" value={record.company_name} />
                              <Row label="Shipping" value={record.shipping_destination || record.country_code} />
                              <Row label="Postal / Zip" value={record.postal_code} />
                            </dl>
                          </section>

                          {/* Quote-specific */}
                          {isQuote && (
                            <>
                              <section>
                                <h4 className="text-xs font-semibold uppercase tracking-wider text-slate-500 mb-1">Print specs</h4>
                                <dl>
                                  <Row label="Material" value={record.material} />
                                  <Row label="Color" value={record.color} />
                                  <Row label="Quality" value={record.quality} />
                                  <Row label="Strength" value={record.strength} />
                                  <Row label="Smoothing" value={record.smoothing} />
                                  <Row label="Quantity" value={record.quantity} />
                                  <Row label="Units" value={record.units} />
                                  <Row label="Volume (cm³)" value={record.volume} />
                                  <Row label="Surface area (cm²)" value={record.surface_area} />
                                  <Row label="Complexity" value={record.complexity_score} />
                                </dl>
                              </section>

                              <section>
                                <h4 className="text-xs font-semibold uppercase tracking-wider text-slate-500 mb-1">Pricing & lead time</h4>
                                <dl>
                                  <Row label="Estimated price" value={money(record.estimated_price)} />
                                  <Row label="Production" value={record.production_time || record.estimated_time} />
                                  <Row label="Delivery" value={record.delivery_weeks ? `${record.delivery_weeks} week(s)` : null} />
                                </dl>
                                {record.price_breakdown && (
                                  <div className="mt-3 p-3 bg-slate-50 border border-slate-200 rounded-lg">
                                    <p className="text-xs font-semibold text-slate-600 mb-2">Price breakdown</p>
                                    <dl className="space-y-1 text-xs">
                                      <Row label="Print cost" value={money(record.price_breakdown.print_cost)} />
                                      <Row label="Smoothing" value={money(record.price_breakdown.smoothing_cost)} />
                                      <Row label="Inserts" value={money(record.price_breakdown.insert_cost)} />
                                      <Row label="Shipping" value={money(record.price_breakdown.shipping_cost)} />
                                      <Row label="Bulk discount" value={money(record.price_breakdown.bulk_discount_amount)} />
                                      <Row label="Visible discount" value={money(record.price_breakdown.visible_discount_amount)} />
                                      <Row label="Total" value={money(record.price_breakdown.total_price)} />
                                    </dl>
                                  </div>
                                )}
                              </section>

                              {record.inserts_required === 'yes' && Array.isArray(record.inserts) && record.inserts.length > 0 && (
                                <section>
                                  <h4 className="text-xs font-semibold uppercase tracking-wider text-slate-500 mb-1">Metal inserts ({record.inserts.length})</h4>
                                  <ul className="space-y-1 text-sm">
                                    {record.inserts.map((m, i) => (
                                      <li key={m.id || i} className="flex items-start gap-2">
                                        <span className="w-5 h-5 rounded-full bg-[#0007b5] text-white text-[10px] font-bold flex items-center justify-center flex-shrink-0 mt-0.5">{i + 1}</span>
                                        <span>{m.type || '—'}{m.size ? ` · ${m.size === 'Other' ? (m.customSize || 'custom') : m.size}` : ''}{m.note ? ` — ${m.note}` : ''}</span>
                                      </li>
                                    ))}
                                  </ul>
                                </section>
                              )}
                            </>
                          )}

                          {/* Service-specific */}
                          {!isQuote && (
                            <section>
                              <h4 className="text-xs font-semibold uppercase tracking-wider text-slate-500 mb-1">Service details</h4>
                              <dl>
                                <Row label="Status" value={record.status} />
                                <Row label="Application" value={record.application} />
                                <Row label="Configuration" value={record.configuration} />
                                <Row label="3D printing" value={record.printing_requirements} />
                                <Row label="Fitment" value={record.fitment_requirements} />
                                <Row label="Timeline" value={record.timeline} />
                                <Row label="Optional services" value={Array.isArray(record.optional_services) ? record.optional_services.join(', ') : record.optional_services} />
                              </dl>
                            </section>
                          )}

                          {/* Description / Notes */}
                          {(record.description || record.notes) && (
                            <section>
                              <h4 className="text-xs font-semibold uppercase tracking-wider text-slate-500 mb-1">{isQuote ? 'Notes' : 'Description'}</h4>
                              <p className="text-sm text-slate-700 whitespace-pre-wrap p-3 bg-slate-50 border border-slate-200 rounded-lg">
                                {record.description || record.notes}
                              </p>
                            </section>
                          )}

                          {/* File */}
                          <section>
                            <h4 className="text-xs font-semibold uppercase tracking-wider text-slate-500 mb-1">Attachment</h4>
                            <div className="flex items-center justify-between p-3 bg-slate-50 border border-slate-200 rounded-lg">
                              <div className="text-sm text-slate-700">
                                {record.filename || 'No file attached'}
                              </div>
                              {canDownload ? (
                                <Button size="sm" onClick={onDownload} data-testid="admin-file-detail-download">
                                  <Download className="w-3 h-3 mr-1" /> Download file
                                </Button>
                              ) : (
                                <Badge variant="outline">No file</Badge>
                              )}
                            </div>
                          </section>
                        </div>

                        <DialogFooter className="mt-6 flex-col sm:flex-row gap-2">
                          <Button
                            variant="outline"
                            onClick={() => {
                              if (isQuote) exportQuoteJson(record.id);
                              else exportServiceRequestJson(record.id);
                            }}
                            data-testid="admin-file-detail-export-json"
                          >
                            <Download className="w-3 h-3 mr-1" /> Download details (JSON)
                          </Button>
                          <Button variant="outline" onClick={() => setFileDetail(null)}>
                            Close
                          </Button>
                        </DialogFooter>
                      </>
                    );
                  })()}
                </DialogContent>
              </Dialog>
            </div>
          </TabsContent>


          {/* Bulk Inventory Editor Dialog */}
          <Dialog open={bulkStockOpen} onOpenChange={(open) => { if (!open) setBulkStockOpen(false); }}>
            <DialogContent className="max-w-3xl max-h-[85vh] overflow-y-auto bg-white text-slate-900 border border-slate-200 shadow-2xl" data-testid="admin-bulk-stock-dialog">
              <DialogHeader>
                <DialogTitle className="flex items-center gap-2">
                  <Package className="w-5 h-5 text-[#0007b5]" />
                  Bulk Edit Inventory
                </DialogTitle>
                <DialogDescription>
                  Update stock quantities for multiple products at once. Only changed rows will be saved.
                </DialogDescription>
              </DialogHeader>

              <div className="mt-3">
                <Input
                  value={bulkStockSearch}
                  onChange={(e) => setBulkStockSearch(e.target.value)}
                  placeholder="Filter by name, SKU or category…"
                  data-testid="admin-bulk-stock-search"
                />
              </div>

              {(() => {
                const q = bulkStockSearch.trim().toLowerCase();
                const rows = (products || []).filter(p => {
                  if (!q) return true;
                  return (
                    (p.name || '').toLowerCase().includes(q) ||
                    (p.sku || '').toLowerCase().includes(q) ||
                    (p.category || '').toLowerCase().includes(q) ||
                    (p.manufacturer || '').toLowerCase().includes(q)
                  );
                });
                const changedCount = (products || []).reduce((n, p) => {
                  const raw = bulkStockDraft[p.id];
                  if (raw === undefined) return n;
                  const parsed = Math.max(0, Math.floor(Number(raw)));
                  if (!Number.isFinite(parsed)) return n;
                  return n + (parsed !== (p.stock_quantity ?? 0) ? 1 : 0);
                }, 0);

                return (
                  <>
                    <div className="mt-4 border border-slate-200 rounded-lg overflow-hidden">
                      <div className="grid grid-cols-[1fr_110px_110px] gap-3 px-3 py-2 bg-slate-50 border-b border-slate-200 text-xs font-semibold uppercase tracking-wider text-slate-600 sticky top-0">
                        <div>Product</div>
                        <div className="text-right">Current</div>
                        <div className="text-right">New</div>
                      </div>
                      <div className="divide-y divide-slate-100 max-h-[45vh] overflow-y-auto">
                        {rows.length === 0 ? (
                          <div className="p-6 text-sm text-slate-500 text-center">No products match your filter.</div>
                        ) : rows.map((p) => {
                          const current = p.stock_quantity ?? 0;
                          const draft = bulkStockDraft[p.id] ?? String(current);
                          const parsed = Number(draft);
                          const isChanged = Number.isFinite(parsed) && Math.max(0, Math.floor(parsed)) !== current;
                          return (
                            <div
                              key={p.id}
                              className={`grid grid-cols-[1fr_110px_110px] gap-3 px-3 py-2 items-center text-sm ${isChanged ? 'bg-amber-50/60' : ''}`}
                              data-testid={`admin-bulk-stock-row-${p.id}`}
                            >
                              <div className="min-w-0">
                                <p className="font-medium text-slate-900 truncate">{p.name}</p>
                                <p className="text-xs text-slate-500 truncate">
                                  {[p.sku, p.category, p.manufacturer].filter(Boolean).join(' · ')}
                                </p>
                              </div>
                              <div className="text-right font-mono text-slate-700">{current}</div>
                              <div>
                                <Input
                                  type="number"
                                  min="0"
                                  step="1"
                                  value={draft}
                                  onChange={(e) => setBulkStockValue(p.id, e.target.value)}
                                  className={`h-8 text-right font-mono ${isChanged ? 'ring-2 ring-amber-300 border-amber-300' : ''}`}
                                  data-testid={`admin-bulk-stock-input-${p.id}`}
                                />
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    </div>

                    <div className="flex items-center justify-between mt-3 text-xs text-slate-500">
                      <span>Showing {rows.length} of {(products || []).length} products.</span>
                      <span>
                        {changedCount > 0
                          ? <span className="font-semibold text-amber-700">{changedCount} pending change{changedCount === 1 ? '' : 's'}</span>
                          : 'No changes yet'}
                      </span>
                    </div>
                  </>
                );
              })()}

              <DialogFooter className="mt-6 flex-col sm:flex-row gap-2">
                <Button variant="outline" onClick={() => setBulkStockOpen(false)}>
                  Cancel
                </Button>
                <Button onClick={saveBulkStock} disabled={bulkStockSaving} data-testid="admin-bulk-stock-save">
                  {bulkStockSaving ? 'Saving…' : 'Save changes'}
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>

          {/* Product Reviews Manager Dialog (lives outside Files tab so it works from Products tab) */}
          <Dialog open={!!reviewMgrProduct} onOpenChange={(open) => { if (!open) { setReviewMgrProduct(null); setReviewForm(emptyReviewForm); } }}>
            <DialogContent className="max-w-3xl max-h-[85vh] overflow-y-auto bg-white text-slate-900 border border-slate-200 shadow-2xl" data-testid="admin-reviews-dialog">
              {reviewMgrProduct && (
                <>
                  <DialogHeader>
                    <DialogTitle className="flex items-center gap-2">
                      <Star className="w-5 h-5 text-yellow-500" />
                      Reviews · {reviewMgrProduct.name}
                    </DialogTitle>
                    <DialogDescription>
                      Add, edit or remove customer reviews shown on this product page. Approved reviews appear publicly; the product's aggregate rating is recalculated automatically.
                    </DialogDescription>
                  </DialogHeader>

                  {/* Existing reviews list */}
                  <div className="mt-4">
                    <h4 className="text-sm font-semibold text-slate-700 mb-2">
                      Existing reviews {reviewList.length > 0 ? `(${reviewList.length})` : ''}
                    </h4>
                    {reviewListLoading ? (
                      <p className="text-sm text-slate-500">Loading…</p>
                    ) : reviewList.length === 0 ? (
                      <p className="text-sm text-slate-500 italic">No reviews yet. Use the form below to add one.</p>
                    ) : (
                      <div className="space-y-2">
                        {reviewList.map((r) => (
                          <div key={r.id} className="border border-slate-200 rounded-lg p-3 bg-slate-50" data-testid={`admin-review-row-${r.id}`}>
                            <div className="flex items-start justify-between gap-3">
                              <div className="flex-1 min-w-0">
                                <div className="flex items-center gap-2 mb-1 flex-wrap">
                                  <div className="flex items-center">
                                    {[...Array(5)].map((_, i) => (
                                      <Star key={i} className={`w-3 h-3 ${i < (r.rating || 0) ? 'fill-yellow-400 text-yellow-400' : 'text-slate-300'}`} />
                                    ))}
                                  </div>
                                  <span className="text-sm font-medium text-slate-800">{r.user_name || 'Customer'}</span>
                                  {r.verified_purchase && <Badge variant="outline" className="text-emerald-700 border-emerald-300">Verified</Badge>}
                                  <Badge variant="outline" className={
                                    r.status === 'approved' ? 'text-emerald-700 border-emerald-300' :
                                    r.status === 'rejected' ? 'text-red-700 border-red-300' :
                                    'text-amber-700 border-amber-300'
                                  }>{r.status}</Badge>
                                </div>
                                {r.title && <p className="text-sm font-semibold text-slate-900 mb-0.5">{r.title}</p>}
                                <p className="text-sm text-slate-700 whitespace-pre-wrap">{r.comment}</p>
                              </div>
                              <div className="flex flex-col gap-2 shrink-0">
                                <Button size="sm" variant="outline" onClick={() => setReviewForm({
                                  id: r.id,
                                  user_name: r.user_name || '',
                                  rating: r.rating || 5,
                                  title: r.title || '',
                                  comment: r.comment || '',
                                  verified_purchase: !!r.verified_purchase,
                                  status: r.status || 'approved',
                                })}>
                                  <Edit className="w-3 h-3 mr-1" /> Edit
                                </Button>
                                <Button size="sm" variant="outline" className="text-red-600 border-red-200 hover:bg-red-50" onClick={() => deleteReview(r.id)}>
                                  <Trash2 className="w-3 h-3 mr-1" /> Delete
                                </Button>
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>

                  {/* Add / edit form */}
                  <div className="mt-6 border-t border-slate-200 pt-4">
                    <h4 className="text-sm font-semibold text-slate-700 mb-3">
                      {reviewForm.id ? 'Edit review' : 'Add a new review'}
                    </h4>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                      <div>
                        <label className="text-xs font-medium text-slate-600 block mb-1">Reviewer name</label>
                        <Input
                          value={reviewForm.user_name}
                          onChange={(e) => setReviewForm(f => ({ ...f, user_name: e.target.value }))}
                          placeholder="e.g. Alex P."
                          data-testid="admin-review-name-input"
                        />
                      </div>
                      <div>
                        <label className="text-xs font-medium text-slate-600 block mb-1">Rating (1-5)</label>
                        <select
                          className="w-full h-10 px-3 rounded-md border border-slate-300 bg-white text-sm"
                          value={reviewForm.rating}
                          onChange={(e) => setReviewForm(f => ({ ...f, rating: Number(e.target.value) }))}
                          data-testid="admin-review-rating-select"
                        >
                          {[5,4,3,2,1].map(n => <option key={n} value={n}>{n} star{n === 1 ? '' : 's'}</option>)}
                        </select>
                      </div>
                      <div className="md:col-span-2">
                        <label className="text-xs font-medium text-slate-600 block mb-1">Title</label>
                        <Input
                          value={reviewForm.title}
                          onChange={(e) => setReviewForm(f => ({ ...f, title: e.target.value }))}
                          placeholder="e.g. Excellent build quality"
                          data-testid="admin-review-title-input"
                        />
                      </div>
                      <div className="md:col-span-2">
                        <label className="text-xs font-medium text-slate-600 block mb-1">Comment</label>
                        <textarea
                          rows={4}
                          className="w-full px-3 py-2 rounded-md border border-slate-300 bg-white text-sm resize-y"
                          value={reviewForm.comment}
                          onChange={(e) => setReviewForm(f => ({ ...f, comment: e.target.value }))}
                          placeholder="Short, specific and concrete wins trust."
                          data-testid="admin-review-comment-input"
                        />
                      </div>
                      <div>
                        <label className="text-xs font-medium text-slate-600 block mb-1">Status</label>
                        <select
                          className="w-full h-10 px-3 rounded-md border border-slate-300 bg-white text-sm"
                          value={reviewForm.status}
                          onChange={(e) => setReviewForm(f => ({ ...f, status: e.target.value }))}
                          data-testid="admin-review-status-select"
                        >
                          <option value="approved">Approved (public)</option>
                          <option value="pending">Pending</option>
                          <option value="rejected">Rejected / hidden</option>
                        </select>
                      </div>
                      <div className="flex items-center gap-2 mt-6">
                        <input
                          id="admin-review-verified"
                          type="checkbox"
                          checked={reviewForm.verified_purchase}
                          onChange={(e) => setReviewForm(f => ({ ...f, verified_purchase: e.target.checked }))}
                          data-testid="admin-review-verified-checkbox"
                        />
                        <label htmlFor="admin-review-verified" className="text-sm text-slate-700">Show "Verified Purchase" badge</label>
                      </div>
                    </div>

                    <div className="flex items-center justify-end gap-2 mt-4">
                      {reviewForm.id && (
                        <Button variant="ghost" onClick={() => setReviewForm(emptyReviewForm)}>
                          Cancel edit
                        </Button>
                      )}
                      <Button onClick={saveReview} disabled={reviewSaving} data-testid="admin-review-save-btn">
                        {reviewSaving ? 'Saving…' : (reviewForm.id ? 'Save changes' : 'Add review')}
                      </Button>
                    </div>
                  </div>

                  <DialogFooter className="mt-6">
                    <Button variant="outline" onClick={() => { setReviewMgrProduct(null); setReviewForm(emptyReviewForm); }}>
                      Close
                    </Button>
                  </DialogFooter>
                </>
              )}
            </DialogContent>
          </Dialog>


          {/* Newsletter Tab */}
          <TabsContent value="newsletter" className="space-y-6">
            <Card>
              <CardHeader>
                <CardTitle>Newsletter Subscribers</CardTitle>
                <CardDescription>Manage email subscribers for your newsletter</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="mb-4">
                  <Input
                    placeholder="Search by email..."
                    value={newsletterSearchTerm}
                    onChange={(e) => setNewsletterSearchTerm(e.target.value)}
                    className="max-w-sm"
                  />
                </div>
                
                <div className="rounded-md border">
                  <table className="w-full">
                    <thead>
                      <tr className="border-b bg-slate-50">
                        <th className="p-3 text-left font-medium">Email</th>
                        <th className="p-3 text-left font-medium">Name</th>
                        <th className="p-3 text-left font-medium">Location</th>
                        <th className="p-3 text-left font-medium">Subscribed Date</th>
                        <th className="p-3 text-left font-medium">Status</th>
                        <th className="p-3 text-right font-medium">Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {newsletterSubscribers
                        .filter(sub => 
                          sub.email.toLowerCase().includes(newsletterSearchTerm.toLowerCase())
                        )
                        .map((subscriber) => (
                          <tr key={subscriber.id} className="border-b hover:bg-slate-50">
                            <td className="p-3">
                              <div className="font-medium">{subscriber.email}</div>
                            </td>
                            <td className="p-3 text-sm text-slate-600">
                              {subscriber.first_name || subscriber.last_name ? (
                                <span>{subscriber.first_name} {subscriber.last_name}</span>
                              ) : (
                                <span className="text-slate-400 italic">Not provided</span>
                              )}
                            </td>
                            <td className="p-3 text-sm text-slate-600">
                              {subscriber.city || subscriber.state_province || subscriber.country ? (
                                <div>
                                  {subscriber.city && <div>{subscriber.city}</div>}
                                  {subscriber.state_province && <div>{subscriber.state_province}</div>}
                                  {subscriber.country && <div>{subscriber.country}</div>}
                                </div>
                              ) : (
                                <span className="text-slate-400 italic">Not provided</span>
                              )}
                            </td>
                            <td className="p-3 text-sm text-slate-600">
                              {new Date(subscriber.subscribed_at).toLocaleDateString()}
                            </td>
                            <td className="p-3">
                              {subscriber.profile_completed ? (
                                <Badge variant="default" className="bg-green-100 text-green-800 border-green-200">Complete</Badge>
                              ) : (
                                <Badge variant="outline" className="text-slate-600">Email Only</Badge>
                              )}
                            </td>
                            <td className="p-3 text-right">
                              <Button
                                onClick={() => removeNewsletterSubscriber(subscriber.email)}
                                variant="destructive"
                                size="sm"
                              >
                                <Trash2 className="w-4 h-4" />
                              </Button>
                            </td>
                          </tr>
                        ))}
                    </tbody>
                  </table>
                  
                  {newsletterSubscribers.filter(sub => 
                    sub.email.toLowerCase().includes(newsletterSearchTerm.toLowerCase())
                  ).length === 0 && (
                    <div className="py-8 text-center text-slate-500">
                      No subscribers found
                    </div>
                  )}
                </div>
                
                <div className="mt-4 text-sm text-slate-600">
                  Total subscribers: {newsletterSubscribers.length}
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* ───────── Metal Insert Pricing ───────── */}
          <TabsContent value="inserts" className="space-y-6">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center space-x-2">
                  <Pin className="w-5 h-5" />
                  <span>Metal Insert Pricing</span>
                </CardTitle>
                <CardDescription>
                  Set the base price for the smallest preset size and a per-size compound escalation rate for each (type × unit) combination. Each subsequent preset size costs <code>base × (1 + rate)^(size index − 1)</code>. "Other" sizes use the last preset's rate.
                </CardDescription>
              </CardHeader>
              <CardContent>
                {insertPricingLoading && insertPricing.length === 0 ? (
                  <div className="py-12 text-center text-slate-500">Loading pricing…</div>
                ) : (
                  <div className="space-y-4">
                    <div className="grid grid-cols-12 gap-3 px-4 py-2 text-xs font-semibold text-slate-500 uppercase tracking-wide">
                      <div className="col-span-3">Type</div>
                      <div className="col-span-3">Units</div>
                      <div className="col-span-3">Base price (smallest size)</div>
                      <div className="col-span-3">Escalation rate (%)</div>
                    </div>
                    {insertPricing.map((row, i) => (
                      <div key={`${row.type}-${row.units}`} className="grid grid-cols-12 gap-3 px-4 py-3 bg-white border border-slate-200 rounded-xl items-center">
                        <div className="col-span-3 text-sm font-semibold text-slate-900 capitalize">{row.type === 'threaded' ? 'Threaded insert' : 'Bushing'}</div>
                        <div className="col-span-3 text-sm text-slate-600 capitalize">{row.units}</div>
                        <div className="col-span-3">
                          <div className="relative">
                            <span className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 text-sm">$</span>
                            <Input
                              type="number"
                              step="0.01"
                              min="0"
                              value={row.base_price}
                              onChange={(e) => {
                                const next = [...insertPricing];
                                next[i] = { ...next[i], base_price: e.target.value === '' ? '' : parseFloat(e.target.value) };
                                setInsertPricing(next);
                              }}
                              className="pl-7"
                              data-testid={`insert-base-price-${row.type}-${row.units}`}
                            />
                          </div>
                        </div>
                        <div className="col-span-3">
                          <div className="relative">
                            <Input
                              type="number"
                              step="0.1"
                              min="0"
                              value={row.escalation_rate == null ? '' : (row.escalation_rate * 100).toFixed(2)}
                              onChange={(e) => {
                                const raw = e.target.value;
                                const pct = raw === '' ? 0 : parseFloat(raw) / 100;
                                const next = [...insertPricing];
                                next[i] = { ...next[i], escalation_rate: pct };
                                setInsertPricing(next);
                              }}
                              className="pr-8"
                              data-testid={`insert-rate-${row.type}-${row.units}`}
                            />
                            <span className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 text-sm">%</span>
                          </div>
                        </div>
                      </div>
                    ))}
                    <div className="flex items-center gap-3 pt-3">
                      <Button
                        onClick={async () => {
                          try {
                            setInsertPricingMessage(null);
                            setInsertPricingLoading(true);
                            const idToken = await getIdToken();
                            const res = await fetch(`${process.env.REACT_APP_BACKEND_URL || ''}/api/admin/insert-pricing`, {
                              method: 'PUT',
                              headers: { 'Authorization': `Bearer ${idToken}`, 'Content-Type': 'application/json' },
                              body: JSON.stringify({ rows: insertPricing })
                            });
                            if (!res.ok) throw new Error(await res.text());
                            const data = await res.json();
                            setInsertPricing(data.rows || []);
                            setInsertPricingMessage({ type: 'success', text: 'Pricing saved.' });
                          } catch (err) {
                            setInsertPricingMessage({ type: 'error', text: `Save failed: ${err.message || err}` });
                          } finally {
                            setInsertPricingLoading(false);
                          }
                        }}
                        disabled={insertPricingLoading}
                        data-testid="insert-pricing-save"
                      >
                        {insertPricingLoading ? 'Saving…' : 'Save Pricing'}
                      </Button>
                      {insertPricingMessage && (
                        <span className={`text-sm ${insertPricingMessage.type === 'success' ? 'text-green-600' : 'text-red-600'}`}>
                          {insertPricingMessage.text}
                        </span>
                      )}
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>


        </Tabs>
      </div>

      {/* Waitlist subscribers modal */}
      <Dialog open={!!waitlistModal} onOpenChange={(open) => !open && setWaitlistModal(null)}>
        <DialogContent className="max-w-2xl bg-white text-slate-900 border border-slate-200 shadow-2xl" data-testid="waitlist-subscribers-modal">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Bell className="w-5 h-5 text-[#0007b5]" />
              Back-in-Stock Waitlist
            </DialogTitle>
            <DialogDescription>
              {waitlistModal?.product?.name && (
                <span>Customers waiting for <strong>{waitlistModal.product.name}</strong> to be back in stock.</span>
              )}
            </DialogDescription>
          </DialogHeader>
          <div className="max-h-[60vh] overflow-y-auto">
            {waitlistModal?.loading ? (
              <div className="py-10 text-center text-slate-500 text-sm">Loading subscribers…</div>
            ) : !waitlistModal?.entries?.length ? (
              <div className="py-10 text-center text-slate-500 text-sm">No customers on the waitlist yet.</div>
            ) : (
              <div className="divide-y divide-slate-100 border border-slate-200 rounded-lg">
                {waitlistModal.entries.map((entry) => (
                  <div key={entry.id} className="flex items-center justify-between px-4 py-3 gap-3">
                    <div className="min-w-0">
                      <div className="flex items-center gap-2">
                        <span className="font-semibold text-slate-900 truncate">{entry.name || entry.email.split('@')[0]}</span>
                        {entry.notified ? (
                          <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-semibold bg-emerald-50 text-emerald-700 border border-emerald-200">
                            <CheckCircle className="w-3 h-3" />
                            Notified
                          </span>
                        ) : (
                          <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-semibold bg-amber-50 text-amber-700 border border-amber-200">
                            <Clock className="w-3 h-3" />
                            Pending
                          </span>
                        )}
                      </div>
                      <div className="text-xs text-slate-500 truncate">{entry.email}</div>
                    </div>
                    <div className="text-xs text-slate-500 text-right flex-shrink-0">
                      <div>Joined {new Date(entry.created_at).toLocaleDateString()}</div>
                      {entry.notified && entry.notified_at && (
                        <div className="text-emerald-600">Emailed {new Date(entry.notified_at).toLocaleDateString()}</div>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
          {waitlistModal?.entries?.length > 0 && (
            <div className="pt-2 text-xs text-slate-500">
              {waitlistModal.entries.filter(e => !e.notified).length} pending ·{' '}
              {waitlistModal.entries.filter(e => e.notified).length} notified · {waitlistModal.entries.length} total
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AdminPanel;